import * as THREE from 'https://esm.sh/three@0.169.0';
import { EffectComposer } from 'https://esm.sh/three@0.169.0/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'https://esm.sh/three@0.169.0/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'https://esm.sh/three@0.169.0/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'https://esm.sh/three@0.169.0/examples/jsm/postprocessing/OutputPass.js';
import { OrbitControls } from 'https://esm.sh/three@0.169.0/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'https://esm.sh/three@0.169.0/examples/jsm/environments/RoomEnvironment.js';
import { RoundedBoxGeometry } from 'https://esm.sh/three@0.169.0/examples/jsm/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'https://esm.sh/three@0.169.0/examples/jsm/utils/BufferGeometryUtils.js';
import {
  bakeRigidGroup as bakeRigidGroupMeshes,
  createAssetKit,
  createVegetationKit,
  lerpAngle,
  makeLoopPath,
  yawFromDir,
} from './dawn-ds-city-hero-scene-kit.js';
import { createCityHeroCityBuilders } from './dawn-ds-city-hero-city-builders.js';

function initCityHero(root){
  if (!root || root.__cityHeroReady) return;
  root.__cityHeroReady = true;

/* ============================================================ */
/* 1. CONSTANTS, RNG, PALETTE                                   */
/* ============================================================ */
// island city: 54-unit superblock pitch, full-scale HQ at the centre,
// four ring-2 blocks on the axes, rounded coastline + open water all around
const L = [-135, -81, -27, 27, 81, 135];       // road centre lines (both axes)
const NL = L.length;
const ISLAND_R = 165;                          // island radius (coastline)
const ROAD_EXT = c => { const a = Math.abs(c); return a > 100 ? 86 : a > 50 ? 132 : 140; };
const PLAT_HALF = 140;                         // playable extent
const BLOCK_C = [-108, -54, 0, 54, 108];       // block centres
const HQ_STOP_R = 26;                          // monster stops here and pounds the shield
const MONSTER_HP = 240; // hero demo threat: tuned for manual pacing tests
const MON_SCALE = 2.6;
const MONSTER_BASE_SPEED = 5.5;
const MONSTER_BERSERK_AT = .3;
const MONSTER_BERSERK_MULT = 2;
const MONSTER_KNOCK_TIME = .22;
const MONSTER_KNOCK_DIST = 3.1;
const MONSTER_ATTACK_ANIM = .34;
const MONSTER_ATTACK_RANGE = 13;
const DRONE_MAX_HITS = 2;
const DRONE_LASER_DAMAGE = 2;
const CINEMATIC_TIME_SCALE = .22;

function mulberry32(a){ return function(){ a|=0; a=a+0x6D2B79F5|0; let t=Math.imul(a^a>>>15,1|a);
  t=t+Math.imul(t^t>>>7,61|t)^t; return ((t^t>>>14)>>>0)/4294967296; }; }
const rng = mulberry32(20260612);
const pick = arr => arr[Math.floor(rng()*arr.length)];

const PAL = {
  bg: 0xe9ebef, ground: 0xe2e5ea, pond: 0xdfe9f8,
  outskirt: [0xedf0f7, 0xeaedf5, 0xf1f3f9],
  shirt: [0xffb9b3, 0x9fdcc8, 0xaecbfa, 0xffd9a0, 0xd8c2f5],
  skin: 0xffe4cd,
  monsterBody: 0xb9a3e6, monsterLimb: 0xa78fd9, monsterSpike: 0x8d6fce, monsterBelly: 0xe7ddf8,
};

/* ============================================================ */
/* 2. RENDERER / SCENE / CAMERA / LIGHTS                        */
/* ============================================================ */
const stage = root.querySelector('[data-city-stage]');
const canvas = root.querySelector('[data-city-canvas]');
const loadingEl = root.querySelector('[data-city-loading]');
const isMobileViewport = () => window.matchMedia('(max-width: 700px)').matches;
const renderPixelRatio = (w, h) => {
  const deviceRatio = window.devicePixelRatio || 1;
  const maxRatio = isMobileViewport() ? 3 : (w * h > 1200000 ? 1.35 : 1.5);
  return Math.min(deviceRatio, maxRatio);
};
let renderer;
try {
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    stencil: false,
    powerPreference: 'high-performance',
  });
} catch (e) {
  if (loadingEl) loadingEl.textContent = 'WEBGL IS NOT AVAILABLE IN THIS BROWSER';
  throw e;
}
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.NeutralToneMapping;
renderer.toneMappingExposure = 1.02;
renderer.outputColorSpace = THREE.SRGBColorSpace;

const scene = new THREE.Scene();
scene.background = new THREE.Color(PAL.bg);
scene.fog = new THREE.Fog(PAL.bg, 400, 900); // ranges retuned every frame to follow the camera

// environment map so the refractive glass has something real to reflect
const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), .04).texture;
scene.environmentIntensity = .3;

const ASPECT = 16/9;
const camera = new THREE.PerspectiveCamera(24, ASPECT, 5, 1600);
const DESKTOP_CAMERA_POSES = {
  hero:     { pos: new THREE.Vector3(34.67, 37.87, 83.55), look: new THREE.Vector3(-30.75, 16.38, -11.9) },
  outcome:  { pos: new THREE.Vector3(54.51, 40.89, 69.76), look: new THREE.Vector3(-10.23, 18.74, -26.2) },
  tactical: { pos: new THREE.Vector3(-145, 245, 280), look: new THREE.Vector3(0, 2, 0) },
};
const CAMERA_POSES = {
  desktop: DESKTOP_CAMERA_POSES,
  tablet: DESKTOP_CAMERA_POSES,
  mobile: {
    hero:     { pos: new THREE.Vector3(42.40, 62.57, 187.40), look: new THREE.Vector3(-18.81, 6.62, -15.19) },
    outcome:  { pos: new THREE.Vector3(-118.19, 26.41, 169.91), look: new THREE.Vector3(25.88, -5.65, -5.31) },
    tactical: { pos: new THREE.Vector3(-118, 232, 310), look: new THREE.Vector3(0, 4, 0) },
  },
};
const CAMERA_BREAKPOINTS = { mobile: 700, tablet: 1024 };
const clonePose = pose => ({ pos: pose.pos.clone(), look: pose.look.clone(), rate: pose.rate });
const cameraBreakpoint = () => {
  const w = stage.clientWidth || window.innerWidth || 1440;
  if (w <= CAMERA_BREAKPOINTS.mobile) return 'mobile';
  if (w <= CAMERA_BREAKPOINTS.tablet) return 'tablet';
  return 'desktop';
};
let activeCameraBreakpoint = cameraBreakpoint();
let POSE = CAMERA_POSES[activeCameraBreakpoint];
let activeCameraPoseName = 'hero';
camera.position.copy(POSE.hero.pos);

const orbit = new OrbitControls(camera, canvas);
orbit.enableDamping = true; orbit.dampingFactor = .08;
orbit.minDistance = 90; orbit.maxDistance = 440;
orbit.maxPolarAngle = 1.45;
orbit.target.copy(POSE.hero.look);
orbit.update();
let camAnim = null;
let cinematic = null;
let followMonsterCamera = false;
const monsterCameraFollowFocus = new THREE.Vector3();
const monsterCameraFollowDelta = new THREE.Vector3();
const sceneInputActive = () => root.classList.contains('is-city-scroll-active') ||
  root.classList.contains('is-targeting') ||
  root.classList.contains('is-in-battle');
const syncOrbitInput = () => {
  orbit.enabled = sceneInputActive();
};
const setCameraToPose = name => {
  const pose = POSE[name] || CAMERA_POSES.desktop[name] || POSE.hero;
  activeCameraPoseName = name;
  camera.position.copy(pose.pos);
  orbit.target.copy(pose.look);
  orbit.update();
};
const flyTo = (pose, name = null) => {
  camAnim = clonePose(pose);
  if (name) activeCameraPoseName = name;
};
const flyToPose = name => flyTo(POSE[name] || CAMERA_POSES.desktop[name] || POSE.hero, name);
function refreshCameraBreakpoint(){
  const next = cameraBreakpoint();
  if (next === activeCameraBreakpoint) return;
  activeCameraBreakpoint = next;
  POSE = CAMERA_POSES[next] || CAMERA_POSES.desktop;
  if (activeCameraPoseName && !camAnim && !cinematic) setCameraToPose(activeCameraPoseName);
}
orbit.addEventListener('start', () => {
  camAnim = null; cinematic = null; activeCameraPoseName = null; followMonsterCamera = false;
});
syncOrbitInput();
const orbitInputEvents = [
  'agent-force:drop-armed',
  'agent-force:monster-detected',
  'agent-force:city-scroll-active',
  'agent-force:city-scroll-inactive',
  'agent-force:threat-neutralized',
  'agent-force:problem-selected',
  'agent-force:scroll-down',
];
orbitInputEvents.forEach(name => root.addEventListener(name, syncOrbitInput));
const appliedShake = new THREE.Vector3();

scene.add(new THREE.HemisphereLight(0xffffff, 0xe6ebf2, .8));
const sun = new THREE.DirectionalLight(0xfff6ea, 1.35);
sun.position.set(52, 88, 48);
sun.castShadow = true;
const shadowMapSize = isMobileViewport() ? 1024 : 2048;
sun.shadow.mapSize.set(shadowMapSize, shadowMapSize);
sun.shadow.camera.left = -175; sun.shadow.camera.right = 175;
sun.shadow.camera.top = 175; sun.shadow.camera.bottom = -175;
sun.shadow.camera.near = 10; sun.shadow.camera.far = 360;
sun.shadow.bias = -0.0004; sun.shadow.normalBias = 0.05;
scene.add(sun);

let composer, bloomPass;
let activeRenderPixelRatio = 1;
function resize(){
  const w = Math.max(1, stage.clientWidth), h = Math.max(1, stage.clientHeight);
  refreshCameraBreakpoint();
  const pixelRatio = renderPixelRatio(w, h);
  activeRenderPixelRatio = pixelRatio;
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
  renderer.setSize(w, h, false);
  renderer.setPixelRatio(pixelRatio);
  if (composer){
    composer.setSize(w, h);
    composer.setPixelRatio(pixelRatio);
  }
}
const resizeObserver = new ResizeObserver(resize);
resizeObserver.observe(stage);
resize();

composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
bloomPass = new UnrealBloomPass(
  new THREE.Vector2(stage.clientWidth, stage.clientHeight), .26, .45, 1.55);
composer.addPass(bloomPass);
composer.addPass(new OutputPass());
resize();

// live style tuning, persisted across reloads
const TUNE_DEFAULT = { halo: .26, glow: 1 };
const TUNE = { ...TUNE_DEFAULT };
try { Object.assign(TUNE, JSON.parse(localStorage.getItem('ks2-tune') || '{}')); } catch(e){}
function applyTune(){
  TUNE.halo = Math.min(.7, Math.max(0, +TUNE.halo));
  TUNE.glow = Math.min(2, Math.max(.3, +TUNE.glow || TUNE_DEFAULT.glow));
  bloomPass.strength = TUNE.halo;
  GLOW.emissiveIntensity = 1.15 * TUNE.glow;
  SOFT.emissiveIntensity = .38 * TUNE.glow;
  RIM.emissiveIntensity = 1.6 * TUNE.glow;
  try { localStorage.setItem('ks2-tune', JSON.stringify(TUNE)); } catch(e){}
}

/* ============================================================ */
/* 3. SHARED MATERIALS - the mint design kit from models/        */
/* ============================================================ */
const MINT = 0x49eec6;
const std = o => new THREE.MeshStandardMaterial({ roughness: .9, metalness: 0, ...o });
const WHITE = std({ color: 0xffffff, roughness: .58 });
const GLOW  = std({ color: 0xeafffa, emissive: MINT, emissiveIntensity: 1.15, roughness: .4 });
const SOFT  = std({ color: 0xcdf8ec, emissive: MINT, emissiveIntensity: .38, roughness: .55 });
const BAND  = std({ color: 0x4ce8c2, emissive: MINT, emissiveIntensity: .3, roughness: .5 });
const SCREEN = std({ color: 0x9ff5dd, emissive: MINT, emissiveIntensity: .55, roughness: .4 });
const PAVE  = std({ color: 0xf3f4f6, roughness: .85 });
const JOINT = std({ color: 0xe3e6eb, roughness: .85 });
const ASPH  = std({ color: 0xb2b7c0, roughness: .95 });
const PAINT = std({ color: 0xf5f7fa, roughness: .8 });
const VENT  = std({ color: 0x9aa3b5 });
const VENT2 = std({ color: 0xc3c9d4 });
const DARKM = std({ color: 0x5d6675 });
const TRUNK = std({ color: 0x8a6f52 });
const GRASS = std({ color: 0x8cc356, roughness: .9 });
const GREEN = [std({ color: 0x7cab51 }), std({ color: 0x69984a }), std({ color: 0x8fbc5e })];
const LEAF  = [std({ color: 0x86b958 }), std({ color: 0x6fa34a }), std({ color: 0x9ccb66 })];
const INK = std({ color: 0x8b94a3, roughness: .45, metalness: .1 });
const RIM = std({ color: 0xbfffe9, emissive: MINT, emissiveIntensity: 1.6, roughness: .4 });

// real refractive glass: transmission + ior + mint attenuation
const mkGlass = () => new THREE.MeshPhysicalMaterial({ color: 0xbdf6e6, emissive: MINT,
  emissiveIntensity: .16, roughness: .14, metalness: 0,
  transmission: .92, thickness: .6, ior: 1.45,
  attenuationColor: new THREE.Color(0x7df0d2), attenuationDistance: 2.6,
  clearcoat: 1, clearcoatRoughness: .12, envMapIntensity: 1.15 });
const GLASS = mkGlass();      // framed windows / doors / channel glass
const TIER  = mkGlass();      // twisted-tower tiers - paler, with a uv panel grid
TIER.color.set(0xd2f8ec);
TIER.emissiveIntensity = .26;
TIER.roughness = .2;
TIER.transmission = .72;
TIER.thickness = 1.0;

/* GLSL injection: fresnel rim + travelling holo sweep on the PBR glass,
   optional pool ripple modulation and uv panel-grid seams */
const uTime = { value: 0 };
const holoize = (mat, o = {}) => {
  const f = v => v.toFixed(3);
  if (o.grid) mat.defines = { ...(mat.defines || {}), USE_UV: '' };
  mat.onBeforeCompile = sh => {
    sh.uniforms.uTime = uTime;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;')
      .replace('#include <worldpos_vertex>',
        '#include <worldpos_vertex>\nvWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uTime;\nvarying vec3 vWPos;')
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        {
          vec3 hV = normalize(vViewPosition);
          float hFres = pow(1.0 - clamp(dot(normalize(normal), hV), 0.0, 1.0), 3.0);
          float hSweep = smoothstep(0.80, 0.97,
            fract((vWPos.y - vWPos.x * 0.35 - uTime * ${f(o.speed ?? 1.2)}) / ${f(o.period ?? 11)}));
          float hMod = ${o.ripple
            ? 'sin(vWPos.x * 2.1 + uTime * 1.3) * sin(vWPos.z * 2.3 - uTime * 1.1) * 0.5 + 0.5'
            : '1.0'};
          totalEmissiveRadiance += vec3(0.286, 0.933, 0.776)
            * (hFres * ${f(o.fresnel ?? .5)} + hSweep * ${f(o.sweep ?? .22)} * hMod);
        }`);
    if (o.grid) sh.fragmentShader = sh.fragmentShader
      .replace('#include <color_fragment>', `#include <color_fragment>
        {
          float gx = smoothstep(0.955, 1.0, abs(fract(vUv.x * ${f(o.grid[0])}) * 2.0 - 1.0));
          float gy = smoothstep(0.930, 1.0, abs(fract(vUv.y * ${f(o.grid[1])}) * 2.0 - 1.0));
          diffuseColor.rgb *= 1.0 - min(gx + gy, 1.0) * 0.30;
        }`);
  };
};
holoize(GLASS, { fresnel: .5, sweep: .22, speed: 1.2, period: 11 });
holoize(TIER,  { fresnel: .3, sweep: .18, speed: 1.2, period: 11, grid: [16, 3] });

/* procedural water: wave-displaced surface, fresnel depth color, sun glints.
   oct > 0 clips the plane to an octagon of that apothem around (cx, cz) */
const mkWater = (o = {}) => new THREE.ShaderMaterial({
  uniforms: {
    uTime,
    uSun:  { value: new THREE.Vector3(26, 44, 24).normalize() },
    uDeep: { value: new THREE.Color(o.deep ?? 0x3fd4ae) },
    uPale: { value: new THREE.Color(o.pale ?? 0xc9fbee) },
    uC:    { value: new THREE.Vector2(o.cx || 0, o.cz || 0) },
    uOct:  { value: o.oct || 0 },
  },
  vertexShader: `
    uniform float uTime;
    varying vec3 vWorld;
    varying vec3 vNorm;
    float h(vec2 p){
      float t = uTime;
      return sin(p.x*1.6 + t*1.1) * sin(p.y*1.4 - t*.9) * .045
           + sin((p.x + p.y)*2.7 + t*1.7) * .028
           + sin(p.x*4.2 - t*2.3) * sin(p.y*3.9 + t*2.0) * .016;
    }
    void main(){
      vec3 wp = (modelMatrix * vec4(position, 1.0)).xyz;
      float e = .15, h0 = h(wp.xz);
      wp.y += h0;
      float hx = h(wp.xz + vec2(e, 0.)) - h0;
      float hz = h(wp.xz + vec2(0., e)) - h0;
      vNorm = normalize(vec3(-hx/e, 1.0, -hz/e));
      vWorld = wp;
      gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
    }`,
  fragmentShader: `
    uniform float uTime;
    uniform vec3 uSun, uDeep, uPale;
    uniform vec2 uC;
    uniform float uOct;
    varying vec3 vWorld;
    varying vec3 vNorm;
    void main(){
      if (uOct > 0.0){
        vec2 p = vWorld.xz - uC;
        float oct = max(max(abs(p.x), abs(p.y)), (abs(p.x) + abs(p.y)) * .7071);
        if (oct > uOct) discard;
      }
      vec3 n = normalize(vNorm);
      vec3 v = normalize(cameraPosition - vWorld);
      float fres = pow(1.0 - clamp(dot(n, v), 0.0, 1.0), 2.5);
      float caust = sin(vWorld.x*5.3 + uTime*1.2) * sin(vWorld.z*4.7 - uTime*1.5) * .5 + .5;
      vec3 col = mix(uDeep, uPale, fres*.75 + caust*.12);
      vec3 r = reflect(-uSun, n);
      float spec = pow(max(dot(r, v), 0.0), 80.0) * .9
                 + pow(max(dot(r, v), 0.0), 400.0) * 2.2;
      // dissolve toward the sky haze at distance (ShaderMaterial ignores scene fog)
      float fd = length(cameraPosition - vWorld);
      vec3 outCol = mix(col + vec3(spec), vec3(0.914, 0.922, 0.937), smoothstep(280.0, 800.0, fd));
      gl_FragColor = vec4(outCol, 1.0);
    }`,
});

// open-sea variant of the water shader: ocean-scale swell (the pool shader's
// centimetre waves vanish at island scale), ripple sparkle that fades with
// distance, and animated foam along the island and islet shorelines
const mkSea = (o = {}) => new THREE.ShaderMaterial({
  uniforms: {
    uTime,
    uSun:  { value: new THREE.Vector3(26, 44, 24).normalize() },
    uDeep: { value: new THREE.Color(o.deep ?? 0x2fbfa4) },
    uPale: { value: new THREE.Color(o.pale ?? 0xc2f3e6) },
    uShore: { value: [               // xz centre + foam radius of every coastline
      new THREE.Vector3(0, 0, 171),  // main island (beach rim)
      new THREE.Vector3(-235, 90, 18),
      new THREE.Vector3(215, -150, 14),
      new THREE.Vector3(60, 245, 16),
      new THREE.Vector3(-180, -210, 12),
    ] },
  },
  vertexShader: `
    uniform float uTime;
    varying vec3 vWorld;
    varying vec3 vNorm;
    float swell(vec2 p){
      float t = uTime;
      return sin(p.x*.062 + t*.50) * sin(p.y*.054 - t*.38) * .42
           + sin((p.x + p.y)*.092 + t*.62) * .26
           + sin(p.x*.150 - t*.85) * sin(p.y*.135 + t*.74) * .14;
    }
    void main(){
      vec3 wp = (modelMatrix * vec4(position, 1.0)).xyz;
      float e = 2.5, h0 = swell(wp.xz);
      wp.y += h0;
      float hx = swell(wp.xz + vec2(e, 0.)) - h0;
      float hz = swell(wp.xz + vec2(0., e)) - h0;
      vNorm = normalize(vec3(-hx/e, 1.0, -hz/e));
      vWorld = wp;
      gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
    }`,
  fragmentShader: `
    uniform float uTime;
    uniform vec3 uSun, uDeep, uPale;
    uniform vec3 uShore[5];
    varying vec3 vWorld;
    varying vec3 vNorm;
    void main(){
      float t = uTime;
      vec2 p = vWorld.xz;
      float dCam = length(cameraPosition - vWorld);
      // fine ripple detail on the swell normal, faded out before it can shimmer
      float det = 1.0 - smoothstep(60.0, 420.0, dCam);
      vec2 g1 = vec2(cos(p.x*.55 + t*1.6), cos(p.y*.48 - t*1.3));
      vec2 g2 = vec2(cos((p.x + p.y)*.85 - t*2.1), cos((p.y - p.x)*.78 + t*1.8));
      vec3 n = normalize(vec3(vNorm.x + (g1.x*.10 + g2.x*.05)*det, 1.0,
                              vNorm.z + (g1.y*.10 + g2.y*.05)*det));
      vec3 v = normalize(cameraPosition - vWorld);
      float fres = pow(1.0 - clamp(dot(n, v), 0.0, 1.0), 2.2);
      // broad colour patches drifting with the swell ('patch' is reserved in GLSL)
      float tone = sin(p.x*.045 + t*.22) * sin(p.y*.038 - t*.18) * .5 + .5;
      vec3 col = mix(uDeep, uPale, clamp(fres*.45 + tone*.10, 0.0, 1.0));
      // animated foam collar along every shoreline
      float sd = 1e5;
      for (int i = 0; i < 5; i++) sd = min(sd, length(p - uShore[i].xy) - uShore[i].z);
      float wob = sin(sd*.9 - t*1.2)*1.4 + sin(p.x*.18 + p.y*.16 + t*.8)*1.1;
      float foam = 1.0 - smoothstep(1.0, 8.0, sd + wob);
      col = mix(col, vec3(.97, 1.0, .99), clamp(foam, 0.0, 1.0) * (.55 + .25*sin(t*2.0 + sd*2.2)));
      // sun glints ride the rippled normal - sparkle up close, calm matte far out
      // (without the fade, smooth distant water mirrors the sun as a white sheet)
      vec3 r = reflect(-uSun, n);
      float spec = (pow(max(dot(r, v), 0.0), 140.0) * .22
                 + pow(max(dot(r, v), 0.0), 600.0) * 2.2) * (.12 + .88*det);
      // dissolve toward the sky haze at distance (ShaderMaterial ignores scene fog)
      vec3 outCol = mix(col + vec3(spec), vec3(0.914, 0.922, 0.937), smoothstep(480.0, 1050.0, dCam));
      gl_FragColor = vec4(outCol, 1.0);
    }`,
});

// unit / vehicle material kit (from models/civilian-car, police-car, mech-robot)
const GLASSM = std({ color: 0x86ead0, emissive: MINT, emissiveIntensity: .32, roughness: .4 });
const HEAD   = std({ color: 0xbcf7e7, emissive: MINT, emissiveIntensity: .55, roughness: .4 });
const MINTLN = std({ color: 0x7df0d2, emissive: MINT, emissiveIntensity: .25 });
const TIRE   = std({ color: 0x454d5e, roughness: .95 });
const HUB    = std({ color: 0x9ff5dd, emissive: MINT, emissiveIntensity: .3 });
const REDTL  = std({ color: 0xf07a72, emissive: 0xe04a42, emissiveIntensity: .35 });
const MINTTL = std({ color: 0x9ff5dd, emissive: MINT, emissiveIntensity: .4 });
const copMintMat = std({ color: 0x9ff5dd, emissive: MINT, emissiveIntensity: 1.2 });
const copBlueMat = std({ color: 0xa9c6ff, emissive: 0x2f6fe8, emissiveIntensity: 1.2 });

// game-side materials
const beaconMat = std({ color: 0xffffff, emissive: 0xf4544c, emissiveIntensity: 0 });
const shieldMat = new THREE.MeshBasicMaterial({ color: 0x8df2d6, transparent: true, opacity: 0, depthWrite: false });
const tracerMintMat = new THREE.MeshBasicMaterial({ color: 0x7df5d4 });
const tracerBlueMat = new THREE.MeshBasicMaterial({ color: 0x8cc8ff });
applyTune();

/* ============================================================ */
/* 4. SMALL HELPERS                                             */
/* ============================================================ */
const ticks = [];                  // ambient animation hooks from the asset builders
const tick = fn => ticks.push(fn); // fn(dt, t, alarm)

const sceneKitMaterials = { WHITE, GLOW, TRUNK, GRASS, GREEN, LEAF };
const bakeRigidGroup = group => bakeRigidGroupMeshes(THREE, mergeGeometries, group);
const assetKit = () => createAssetKit(THREE, sceneKitMaterials);
const vegKit = kit => createVegetationKit(sceneKitMaterials, kit);
const makeLoop = makeLoopPath;

// WORKSPACE lettering (canvas texture, mint on white) - shared by the office blocks
const signTex = (() => {
  const cv = document.createElement('canvas'); cv.width = 512; cv.height = 72;
  const x = cv.getContext('2d');
  x.fillStyle = '#ffffff'; x.fillRect(0, 0, 512, 72);
  x.fillStyle = '#38dfb4';
  x.font = '600 40px "Avenir Next", "Segoe UI", sans-serif';
  x.textAlign = 'center'; x.textBaseline = 'middle';
  try { x.letterSpacing = '10px'; } catch(e){}
  x.fillText('WORKSPACE', 261, 40);
  const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace;
  return t;
})();

/* ============================================================ */
/* 5. CITY - ground, roads, blocks (models/ assets), HQ, rail   */
/* ============================================================ */
const city = new THREE.Group();
scene.add(city);

{ // the city sits on a rounded island - beach rim, then open water to the horizon
  const island = new THREE.Mesh(new THREE.CylinderGeometry(ISLAND_R, ISLAND_R + 2.5, 3, 96), std({ color: PAL.ground }));
  island.position.y = -1.5; island.receiveShadow = true;
  city.add(island);
  const beach = new THREE.Mesh(new THREE.CylinderGeometry(ISLAND_R + 4.5, ISLAND_R + 8, 1.7, 96), std({ color: 0xefe8d6 }));
  beach.position.y = -1.0; beach.receiveShadow = true;
  city.add(beach);
  const ocean = new THREE.Mesh(new THREE.PlaneGeometry(1700, 1700, 220, 220),
    mkSea({ deep: 0x21ad92, pale: 0xb8f0e0 }));
  ocean.rotation.x = -Math.PI/2; ocean.position.y = -1.15;
  city.add(ocean);

  const K = assetKit(); city.add(K.root);
  const veg = vegKit(K);

  // small companion islets out in the bay
  for (const [ix, iz, ir] of [[-235, 90, 16], [215, -150, 12], [60, 245, 14], [-180, -210, 10]]){
    const isle = new THREE.Mesh(new THREE.CylinderGeometry(ir, ir + 2, 2.6, 24), std({ color: PAL.ground }));
    isle.position.set(ix, -1.05, iz); isle.receiveShadow = true;
    city.add(isle);
    veg.tree(ix - ir*.3, iz + ir*.2, .25, 1.35, 0, true);
    veg.tree(ix + ir*.35, iz - ir*.25, .25, 1.0, 2);
    veg.shrub(ix + ir*.1, iz + ir*.45, .25, 1.2);
  }

  // corner groves fill the rounded coast wedges between the outer blocks
  for (const [sx, sz] of [[1,1],[1,-1],[-1,1],[-1,-1]]){
    const gx = sx*104, gz = sz*104;
    const lawn = new THREE.Mesh(new THREE.CylinderGeometry(17, 18.5, .5, 24), GRASS);
    lawn.position.set(gx, .22, gz); lawn.receiveShadow = true;
    city.add(lawn);
    veg.tree(gx - 7, gz - 4, .45, 1.5, 0, true);
    veg.tree(gx + 5, gz + 6, .45, 1.25, 1);
    veg.tree(gx + 9, gz - 6, .45, 1.0, 2);
    veg.conifer(gx - 2, gz + 9, .45, 1.1);
    veg.topiary(gx + 1, gz - 9, .45, 1.0);
    veg.shrub(gx - 9, gz + 3, .45, 1.2);
    veg.hedge(gx - 4, gz + 5, .45, 0); veg.hedge(gx - 3.1, gz + 5, .45, 1);
  }

  // palm-ish coastal trees scattered along the beach line
  for (let i = 0; i < 26; i++){
    const a = (i / 26) * Math.PI * 2 + rng()*.15;
    const r = ISLAND_R - 7 - rng()*6;
    const x = Math.cos(a)*r, z = Math.sin(a)*r;
    if (L.some(l => Math.abs(x - l) < 7) || L.some(l => Math.abs(z - l) < 7)) continue; // keep roads clear
    veg.tree(x, z, 0, .9 + rng()*.7, i % 3, rng() < .3);
  }
}

{ // road network: grey asphalt, white centre dashes - no glow (city design decision)
  // each road runs only as far as the island allows (outer roads are short coastal stubs)
  for (const c of L){
    const ext = ROAD_EXT(c);
    const a = new THREE.Mesh(new THREE.BoxGeometry(8, .04, ext*2), ASPH);
    a.position.set(c, .02, 0); a.receiveShadow = true;
    const b = new THREE.Mesh(new THREE.BoxGeometry(ext*2, .04, 8), ASPH);
    b.position.set(0, .02, c); b.receiveShadow = true;
    city.add(a, b);
  }
  // centre dashes (one instanced draw call), skipping the junctions
  const dashGeo = new THREE.BoxGeometry(.3, .03, 2.2);
  const dashes = [];
  for (const c of L){
    const ext = ROAD_EXT(c) - 3;
    for (let p = -ext; p <= ext; p += 7){
      if (L.some(l => Math.abs(p - l) < 7)) continue;
      dashes.push([c, p, 0], [p, c, Math.PI/2]);
    }
  }
  const inst = new THREE.InstancedMesh(dashGeo, PAINT, dashes.length);
  const m4 = new THREE.Matrix4(), q4 = new THREE.Quaternion(),
    up = new THREE.Vector3(0,1,0), one = new THREE.Vector3(1,1,1);
  dashes.forEach(([x, z, rot], i) => {
    q4.setFromAxisAngle(up, rot);
    m4.compose(new THREE.Vector3(x, .045, z), q4, one);
    inst.setMatrixAt(i, m4);
  });
  inst.castShadow = false; inst.receiveShadow = false;
  city.add(inst);
}

const cityBuilders = createCityHeroCityBuilders({
  THREE,
  assetKit,
  vegKit,
  tick,
  rng,
  signTex,
  mkWater,
  WHITE,
  GLOW,
  SOFT,
  GLASS,
  GLASSM,
  PAVE,
  JOINT,
  BAND,
  SCREEN,
  VENT,
  VENT2,
  DARKM,
  GRASS,
  GREEN,
  LEAF,
  TRUNK,
  TIER,
  ASPH,
  PAINT,
});
const {
  PARK_SPOTS,
  buildFountainPark,
  buildCivicBlock,
  buildTwistedPlazaBlock,
  buildGasBlock,
  buildOfficeCourtBlock,
  buildRetailBlock,
  buildSpireDuoBlock,
  buildMixedUseBlock,
  buildQuadCourtBlock,
  buildOfficeParkBlock,
  buildHighrise,
  buildHaloTower,
  buildTwistedTower,
  buildWorkspaceBlock,
  buildLowSlab,
  buildGasStation,
} = cityBuilders;
/* ---- block placement: every parcel on the island is built out ---- */
{
  const place = (g, cx, cz, ry = 0) => {
    g.position.set(cx, 0, cz);
    g.rotation.y = ry;
    city.add(g);
    const c = Math.cos(ry), s = Math.sin(ry);
    for (const p of (g.userData.parkSpots || []))
      PARK_SPOTS.push({ x: cx + p.x*c + p.z*s, z: cz - p.x*s + p.z*c, ry: ry + p.ry });
  };
  // unique landmarks: HQ centre; fountain + halo radar campus flank it;
  // the one twisted tower breaks the east skyline; gas station serves the port road
  place(buildFountainPark(0, 54), 0,  54, 0);          // centrepiece opposite the HQ stair
  place(buildCivicBlock(),       54,   0, -Math.PI/2); // halo radar campus beside the HQ
  place(buildTwistedPlazaBlock(),108, -54, -Math.PI/2);
  place(buildGasBlock(),         54, 108, -Math.PI/2); // forecourt toward the port avenue

  // merged parcels - types scattered so no two neighbouring blocks repeat
  place(buildOfficeCourtBlock(), -54, -54, Math.PI/2);
  place(buildRetailBlock(),        0, -54, 0);
  place(buildSpireDuoBlock(),     54, -54, 0);
  place(buildMixedUseBlock(),    -54,   0, Math.PI/2);
  place(buildQuadCourtBlock(),   -54,  54, 0);
  place(buildOfficeParkBlock(),   54,  54, -Math.PI/2);
  place(buildSpireDuoBlock(),      0, -108, Math.PI);
  place(buildRetailBlock(),      -54, -108, 0);
  place(buildQuadCourtBlock(),    54, -108, Math.PI/2);
  place(buildMixedUseBlock(),   -108,  -54, Math.PI/2);
  place(buildQuadCourtBlock(),  -108,    0, Math.PI/2);
  place(buildOfficeParkBlock(), -108,   54, Math.PI/2);
  place(buildOfficeCourtBlock(), 108,    0, -Math.PI/2);
  place(buildRetailBlock(),      108,   54, -Math.PI/2);
  place(buildOfficeCourtBlock(),   0,  108, Math.PI);
  place(buildRetailBlock(),      -54,  108, Math.PI);
  // pavement under the fountain park so the parcel reads like its neighbours
  const fp = new THREE.Mesh(new THREE.BoxGeometry(46.5, .24, 46.5), PAVE);
  fp.position.set(0, 0, 54); fp.receiveShadow = true; city.add(fp);
}

/* ---- KACHERSOFT HQ - full-scale symmetric art-deco model (hq-model.html) ---- */
const hq = new THREE.Group();
const dronePads = [];
let shieldMesh;
{
  const K = assetKit(), { box, cyl } = K, trim = K.trim;
  const hqRoot = K.root;
  hq.add(hqRoot);
  const poolWater = (cx, cz, S, y) => {
    const w = new THREE.Mesh(new THREE.PlaneGeometry(S, S, 40, 40), mkWater());
    w.rotation.x = -Math.PI/2;
    w.position.set(cx, y, cz);
    hqRoot.add(w);
  };
  const gridGlass = (w, h, x, y, z, cols, rows, axis = 'z', frame = .16) => {
    const F = WHITE;
    if (axis === 'z'){
      box(w, h, .14, GLASS, x, y, z, false);
      box(w + frame*2, frame, .2, F, x, y + h/2 + frame/2, z, false);
      box(w + frame*2, frame, .2, F, x, y - h/2 - frame/2, z, false);
      box(frame, h, .2, F, x - w/2 - frame/2, y, z, false);
      box(frame, h, .2, F, x + w/2 + frame/2, y, z, false);
      for (let i = 1; i < cols; i++) box(.1, h, .18, F, x - w/2 + i*w/cols, y, z, false);
      for (let j = 1; j < rows; j++) box(w, .1, .18, F, x, y - h/2 + j*h/rows, z, false);
    } else {
      box(.14, h, w, GLASS, x, y, z, false);
      box(.2, frame, w + frame*2, F, x, y + h/2 + frame/2, z, false);
      box(.2, frame, w + frame*2, F, x, y - h/2 - frame/2, z, false);
      box(.2, h, frame, F, x, y, z - w/2 - frame/2, false);
      box(.2, h, frame, F, x, y, z + w/2 + frame/2, false);
      for (let i = 1; i < cols; i++) box(.18, h, .1, F, x, y, z - w/2 + i*w/cols, false);
      for (let j = 1; j < rows; j++) box(.18, .1, w, F, x, y - h/2 + j*h/rows, z, false);
    }
  };
  const spire = (x, z, baseY, h, s = 1) => {
    box(.95*s, .34*s, .95*s, WHITE, x, baseY + .17*s, z);
    box(.62*s, .3*s, .62*s, WHITE, x, baseY + .47*s, z);
    const p = cyl(.02, .3*s, h, 4, WHITE, x, baseY + .62*s + h/2, z);
    p.rotation.y = Math.PI/4;
    box(.08*s, .8*s, .08*s, GLOW, x, baseY + .62*s + h*.22, z, false);
  };
  const post = (x, z, baseY, w = .8, h = 1.2) => {
    box(w, h, w, WHITE, x, baseY + h/2, z);
    const p = cyl(.01, w*.46, w*.7, 4, WHITE, x, baseY + h + w*.35, z);
    p.rotation.y = Math.PI/4;
  };
  const bush = (x, z, baseY, s = 1) => {
    box(1.15*s, .95*s, 1.15*s, GREEN[0], x, baseY + .47*s, z);
    box(.8*s, .7*s, .8*s, GREEN[1], x - .55*s, baseY + .35*s, z + .3*s);
    box(.7*s, 1.05*s, .7*s, GREEN[2], x + .5*s, baseY + .52*s, z - .25*s);
  };

  /* ================ PLAZA ================ */
  const PLZ = .8;
  box(48, .4, 42, WHITE, 0, .2, 2);
  box(46, .4, 40, WHITE, 0, .6, 2);
  box(46.4, .07, 40.4, SOFT, 0, .42, 2, false);

  // reflecting pools (mirrored)
  for (const s of [-1, 1]){
    const cx = s*12.8, cz = 12.8, S = 8.4;
    box(S + 1.4, .5, .7, WHITE, cx, PLZ + .25, cz - S/2 - .35);
    box(S + 1.4, .5, .7, WHITE, cx, PLZ + .25, cz + S/2 + .35);
    box(.7, .5, S, WHITE, cx - S/2 - .35, PLZ + .25, cz);
    box(.7, .5, S, WHITE, cx + S/2 + .35, PLZ + .25, cz);
    box(S, .08, S, WHITE, cx, PLZ + .02, cz, false);   // basin floor
    poolWater(cx, cz, S, PLZ + .16);
    cyl(.85, 1.0, .3, 10, WHITE, cx, PLZ + .3, cz);
    spire(cx, cz, PLZ + .42, 2.1, .8);
    post(s*(12.8 - S/2 - .35), cz + S/2 + .35, PLZ, .8, .9);
    post(s*(12.8 + S/2 + .35), cz + S/2 + .35, PLZ, .8, .9);
  }
  // perimeter walls + corner posts
  for (const s of [-1, 1]){
    box(14.6, .85, .55, WHITE, s*14.2, PLZ + .42, 21.6);
    box(.55, .85, 26, WHITE, s*22.7, PLZ + .42, 8.6);
    post(s*22.7, 21.6, PLZ, 1.0, 1.3);
    post(s*6.9, 21.6, PLZ, .8, 1.0);
  }

  /* ================ GRAND STAIR ================ */
  const TER = 3.2;
  box(13, TER - PLZ, 3.6, WHITE, 0, (TER + PLZ)/2, 5.8);
  box(11.4, .05, .5, GLOW, 0, TER + .03, 7.3, false);
  const NSTEP = 10, RISE = (TER - PLZ)/NSTEP, RUN = .56;
  for (let i = 0; i < NSTEP; i++){
    const topY = TER - (i + 1)*RISE, w = 9.4 + i*.26;
    box(w, RISE, RUN + .12, WHITE, 0, topY - RISE/2, 7.6 + (i + .5)*RUN);
    if (i % 3 === 1) box(w - 1.2, .035, RUN - .14, GLOW, 0, topY + .02, 7.6 + (i + .5)*RUN, false);
  }
  box(11.6, .045, 1.3, SOFT, 0, PLZ + .03, 14.0, false);

  // stepped cheek walls + planters (mirrored)
  for (const s of [-1, 1]){
    box(1.7, 2.6, 3.2, WHITE, s*5.6, PLZ + 1.3, 8.6);
    box(1.7, 1.7, 2.6, WHITE, s*5.6, PLZ + .85, 11.4);
    box(1.2, 1.0, 1.2, WHITE, s*5.6, PLZ + .5, 13.6);
    const p = cyl(.01, .5, .8, 4, WHITE, s*5.6, PLZ + 1.4, 13.6);
    p.rotation.y = Math.PI/4;
    bush(s*5.6, 8.6, PLZ + 2.6, 1.0);
    bush(s*5.6, 11.4, PLZ + 1.7, .8);
  }

  /* ================ WINGS (mirrored tiers) ================ */
  for (const s of [-1, 1]){
    // shoulder tier - tall, vertical glazing
    box(4.2, 15.2, 9.5, WHITE, s*7.4, PLZ + 7.6, -2.15);
    gridGlass(2.6, 8.6, s*7.4, 9.2, 2.68, 2, 6);
    box(4.6, .5, 9.9, WHITE, s*7.4, 16.25, -2.15);
    trim(4.66, 9.96, 16.55, s*7.4, -2.15);
    box(.11, 12.5, .11, GLOW, s*9.42, 9.4, 2.62, false);
    spire(s*8.4, 1.4, 16.5, 2.2, .85);
    // drone pad on the shoulder roof
    cyl(1.0, 1.06, .12, 12, WHITE, s*7.4, 16.56, -2.15);
    cyl(.72, .72, .05, 12, GLOW, s*7.4, 16.65, -2.15, false);
    dronePads.push(new THREE.Vector3(s*7.4, 16.7, -2.15));

    // mid tier - broad block, big window grid
    box(5.4, 10, 12.5, WHITE, s*12.2, PLZ + 5, -3.05);
    gridGlass(4.0, 4.6, s*12.2, 7.0, 3.28, 3, 2);
    gridGlass(4.0, 1.5, s*12.2, 3.0, 3.28, 4, 1);
    gridGlass(8.0, 4.6, s*14.92, 7.0, -3.0, 4, 2, 'x');
    box(5.8, .5, 12.9, WHITE, s*12.2, 11.05, -3.05);
    trim(5.86, 12.96, 11.35, s*12.2, -3.05);
    box(.11, 8.5, .11, GLOW, s*14.86, 6.4, 3.2, false);
    spire(s*13.9, 2.4, 11.3, 2.0, .8);

    // outer annex - low, fully glazed
    box(4.2, 6.4, 10.5, WHITE, s*16.9, PLZ + 3.2, -2.45);
    gridGlass(3.2, 3.2, s*16.9, 4.6, 2.86, 2, 2);
    gridGlass(7.6, 3.2, s*19.02, 4.6, -2.4, 4, 2, 'x');
    box(4.6, .45, 10.9, WHITE, s*16.9, 7.25, -2.45);
    trim(4.66, 10.96, 7.52, s*16.9, -2.45);
    post(s*18.6, 2.6, 7.45, .7, .7);
    post(s*18.6, -7.4, 7.45, .7, .7);
  }
  // mid-tier + annex roof pads - six drones cover every wing roof
  for (const s of [-1, 1]){
    cyl(1.0, 1.06, .12, 12, WHITE, s*12.2, 11.36, -3.05);
    cyl(.72, .72, .05, 12, GLOW, s*12.2, 11.45, -3.05, false);
    dronePads.push(new THREE.Vector3(s*12.2, 11.5, -3.05));
    cyl(1.0, 1.06, .12, 12, WHITE, s*16.9, 7.56, -2.45);
    cyl(.72, .72, .05, 12, GLOW, s*16.9, 7.65, -2.45, false);
    dronePads.push(new THREE.Vector3(s*16.9, 7.7, -2.45));
  }

  /* ================ CENTRAL TOWER ================ */
  box(11, 25.7, 12, WHITE, 0, PLZ + 12.85, -2);
  for (const s of [-1, 1]){
    box(1.5, 25.9, 1.5, WHITE, s*5.1, PLZ + 12.95, 3.6);
    box(1.0, 23.2, .8, WHITE, s*2.95, PLZ + 11.6, 4.0);
    box(.12, 18, .12, GLOW, s*2.38, 12.4, 4.46, false);
    box(.12, 19.5, .12, GLOW, s*4.32, 13.2, 4.42, false);
    box(.7, 18.6, 1.1, WHITE, s*2.5, 12.4, 3.95);
  }
  gridGlass(3.6, 12.2, 0, 15.5, 4.06, 2, 8);
  box(4.4, .55, 1.1, WHITE, 0, 21.85, 3.95);
  box(3.8, .1, .1, GLOW, 0, 21.55, 4.46, false);

  // entrance portal - stepped surround + mint door
  box(6.2, 6.2, .6, WHITE, 0, TER + 3.1 - .15, 4.15);
  box(5.2, 5.6, .55, WHITE, 0, TER + 2.65, 4.45);
  box(4.4, 5.0, .5, WHITE, 0, TER + 2.42, 4.72);
  gridGlass(2.7, 4.0, 0, TER + 2.06, 5.0, 2, 3);
  box(.14, 4.4, .14, GLOW, -1.62, TER + 2.2, 5.02, false);
  box(.14, 4.4, .14, GLOW, 1.62, TER + 2.2, 5.02, false);
  box(3.5, .14, .14, GLOW, 0, TER + 4.46, 5.02, false);
  box(4.0, .8, .8, WHITE, 0, TER + 5.4, 4.6);
  box(8.4, .9, .9, WHITE, 0, 9.0, 4.15);
  box(7.6, .1, .1, GLOW, 0, 9.5, 4.62, false);
  trim(11.1, 12.1, 26.3, 0, -2);

  /* ================ CROWN ================ */
  box(9.2, 1.3, 10.2, WHITE, 0, 27.15, -2);
  for (let i = 0; i < 10; i++)
    box(.16, .7, .07, GLASS, -3.6 + i*.8, 27.15, 3.14, false);
  trim(9.3, 10.3, 27.85, 0, -2);
  box(7.2, 1.9, 8.2, WHITE, 0, 28.75, -2);
  gridGlass(4.4, 1.2, 0, 28.8, 2.18, 4, 1);
  box(5.0, 1.5, 6.0, WHITE, 0, 30.45, -2);
  trim(5.1, 6.1, 31.25, 0, -2);
  box(3.2, 1.0, 4.0, WHITE, 0, 31.7, -2);
  trim(3.3, 4.1, 32.25, 0, -2);
  spire(0, -2, 32.2, 6.2, 1.15);
  spire(-2.6, -2, 30.0, 4.2, 1.0);  spire(2.6, -2, 30.0, 4.2, 1.0);
  spire(-4.2, -1.4, 27.8, 2.8, .85); spire(4.2, -1.4, 27.8, 2.8, .85);

  // alarm beacons on the crown + wing spire tips
  for (const [bx, by, bz] of [[0, 38.9, -2], [-8.4, 19.6, 1.4], [8.4, 19.6, 1.4]]){
    const bb = new THREE.Mesh(new THREE.SphereGeometry(.22, 10, 10), beaconMat);
    bb.position.set(bx, by, bz); hqRoot.add(bb);
  }

  /* ================ LOGO (3D brand mark from SVG) ================ */
  const logoModelUrl = root.dataset.logoModelUrl;
  if (logoModelUrl) import('https://esm.sh/three@0.169.0/examples/jsm/loaders/GLTFLoader.js')
    .then(({ GLTFLoader }) => new GLTFLoader().load(logoModelUrl, gltf => {
      if (!root.__cityHeroReady) return;
      const inner = new THREE.Group();
      gltf.scene.updateMatrixWorld(true);
      gltf.scene.traverse(o => {
        if (!o.isMesh) return;
        const m = new THREE.Mesh(o.geometry.clone(), INK);
        m.geometry.applyMatrix4(o.matrixWorld);
        m.castShadow = true; inner.add(m);
      });
      if (!inner.children.length) return;
      const bb = new THREE.Box3().setFromObject(inner);
      const c = bb.getCenter(new THREE.Vector3()), sz = bb.getSize(new THREE.Vector3());
      inner.children.forEach(m => m.geometry.translate(-c.x, -c.y, -c.z));
      const rim = inner.clone();
      rim.traverse(o => { if (o.isMesh){ o.material = RIM; o.castShadow = false; } });
      rim.scale.setScalar(1.03); rim.position.y = -sz.y*.35; rim.position.z = sz.z*.05;
      const logo = new THREE.Group(); logo.add(rim, inner);
      const k = 8.4 / sz.x;
      logo.scale.setScalar(k);
      logo.rotation.x = Math.PI/2;
      logo.position.set(0, 24.25, 4.18 + (sz.y*k)/2 + .04);
      hqRoot.add(logo);
      root.__logo3d = true;
      needsRender = true;
      requestFrame();
    }, undefined, () => {}))
    .catch(() => {});

  // shield bubble around the whole compound
  shieldMesh = new THREE.Mesh(new THREE.SphereGeometry(21, 32, 24), shieldMat);
  shieldMesh.position.y = 5; hq.add(shieldMesh);

  // exit ramp through the south wall opening - units glide from the deck down to
  // the kerb; it must end before z=24.4 where the inner traffic lane runs
  const ramp = box(9.2, .3, 3.3, WHITE, 0, .4, 22.55);
  ramp.rotation.x = Math.atan2(.8, 3.3);
}
city.add(hq);

/* ---- elevated monorail: one grand loop around the whole city ---- */
// straights ride above the outer ring roads; the corners sweep out over the beach and water
const RAIL_Y = 11;
const railLoop = makeLoop(135, 54);
{
  const railMat = std({ color: 0xf0f1f4 });
  const n = 220, step = railLoop.len/n;
  for (let i=0;i<n;i++){
    const p = railLoop.at(i*step + step/2);
    const seg = new THREE.Mesh(new THREE.BoxGeometry(step+.15, .4, 1.2), railMat);
    seg.position.set(p.x, RAIL_Y, p.z);
    seg.rotation.y = yawFromDir(p.tx, p.tz);
    seg.castShadow = true; seg.receiveShadow = true;
    city.add(seg);
    if (i % 5 === 0){
      const overWater = Math.hypot(p.x, p.z) > ISLAND_R; // corner pylons stand in the sea
      const h = RAIL_Y + (overWater ? 1.6 : .2);
      const py = new THREE.Mesh(new THREE.BoxGeometry(.6, h, .6), railMat);
      py.position.set(p.x, RAIL_Y - h/2, p.z);
      py.castShadow = true; city.add(py);
    }
  }
}

/* ---- monorail stations: two elevated platforms where the train calls ---- */
const STATION_DS = []; // loop distances of the stop points (mid-train aligned)
{
  const stnMat = std({ color: 0xf0f1f4 });
  const labelTex = (txt) => {
    const cv = document.createElement('canvas'); cv.width = 256; cv.height = 64;
    const x = cv.getContext('2d');
    x.fillStyle = '#ffffff'; x.fillRect(0, 0, 256, 64);
    x.fillStyle = '#11bf9d'; x.font = '700 28px "Avenir Next", "Segoe UI", sans-serif';
    x.textAlign = 'center'; x.textBaseline = 'middle'; x.fillText(txt, 128, 34);
    const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace;
    return t;
  };
  const mkStation = (name, side) => { // side: which side of the track the platform sits on
    const g = new THREE.Group();
    const add = (w, h, d, m, x, y, z, cs = true) => { const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
      b.position.set(x, y, z); b.castShadow = cs; b.receiveShadow = true; g.add(b); return b; };
    const off = side * 2.9, topY = RAIL_Y + .2;
    add(16, .5, 3.4, stnMat, 0, RAIL_Y - .05, off);                 // platform slab
    add(16, .14, 3.5, PAVE, 0, topY + .07, off, false);             // deck surface
    add(16, .08, .3, BAND, 0, topY + .16, off - side*1.55, false);  // warning strip at the rail edge
    for (const sx of [-6.5, 0, 6.5])                                 // columns to the ground
      add(.55, topY, .55, stnMat, sx, topY/2 - .15, off + side*.9);
    for (const sx of [-5.5, 5.5]) add(.4, 3.4, .4, WHITE, sx, topY + 1.85, off + side*1.2);
    add(17, .3, 4.6, WHITE, 0, topY + 3.7, off);                    // canopy
    add(17.2, .18, 4.8, BAND, 0, topY + 3.5, off, false);
    add(15, 1.4, .12, GLASSM, 0, topY + 1.0, off + side*1.6, false);// windscreen
    const sign = new THREE.Mesh(new THREE.PlaneGeometry(5.4, 1.35),
      new THREE.MeshBasicMaterial({ map: labelTex(name) }));
    sign.position.set(0, topY + 2.7, off + side*2.42);
    sign.rotation.y = side > 0 ? 0 : Math.PI;
    g.add(sign);
    // enclosed stair run from the platform end down to the street, plus a lift
    const run = 13, slope = Math.atan2(topY, run);
    const ramp = add(Math.hypot(run, topY), .4, 2.6, stnMat, 8 + run/2, topY/2, off + side*1.0);
    ramp.rotation.z = -slope; // high end meets the platform at x = 8
    const rail1 = add(Math.hypot(run, topY), .12, .12, WHITE, 8 + run/2, topY/2 + 1.0, off + side*1.0 - 1.2, false);
    const rail2 = add(Math.hypot(run, topY), .12, .12, WHITE, 8 + run/2, topY/2 + 1.0, off + side*1.0 + 1.2, false);
    rail1.rotation.z = -slope; rail2.rotation.z = -slope;
    add(1.8, topY + 1.2, 1.8, WHITE, -8.9, (topY + 1.2)/2 - .15, off + side*1.0); // lift shaft
    add(1.84, 1.0, 1.84, GLASSM, -8.9, topY - 1.4, off + side*1.0, false);
    return g;
  };
  for (const [sx, sz, side, name] of [[0, 135, 1, 'BAYFRONT'], [0, -135, -1, 'NORTH BEACH']]){
    const st = mkStation(name, side);
    st.position.set(sx, 0, sz);
    city.add(st);
    let best = 0, bd = 1e9; // find the loop distance closest to the platform centre
    for (let d = 0; d < railLoop.len; d += .25){
      const p = railLoop.at(d); const q = (p.x - sx)*(p.x - sx) + (p.z - sz)*(p.z - sz);
      if (q < bd){ bd = q; best = d; }
    }
    STATION_DS.push((best + 4.3) % railLoop.len); // +4.3 so the middle car centres the platform
  }
}
const train = { cars: [], d: 0, speed: 16, dwell: 0, lastStop: -1 };
for (let i=0;i<3;i++){
  const g = new THREE.Group();
  const add = (w,h,d,m,x,y,z) => { const b = new THREE.Mesh(new THREE.BoxGeometry(w,h,d), m);
    b.position.set(x,y,z); b.castShadow = true; g.add(b); return b; };
  add(3.5, 1.25, 1.5, WHITE, 0, 0, 0);
  add(3.52, .42, 1.52, GLASSM, 0, .12, 0);
  add(3.0, .2, 1.3, WHITE, 0, .72, 0);
  if (i===0) add(.5, .9, 1.3, GLASSM, 1.85, -.05, 0);
  scene.add(g);
  train.cars.push(g);
}

// sea bridge in the background - a straight viaduct crossing the bay with a shuttle train
const shuttle = new THREE.Group();
{
  const vMat = std({ color: 0xf2f0e9 });
  const via = new THREE.Mesh(new THREE.BoxGeometry(900, .5, 1.6), vMat);
  via.position.set(0, 13, -205); via.castShadow = true; city.add(via);
  for (let px = -440; px <= 440; px += 28){
    const p = new THREE.Mesh(new THREE.BoxGeometry(.8, 15.5, .8), vMat);
    p.position.set(px, 5.5, -205); city.add(p);
  }
  const add = (w,h,d,m,x,y,z) => { const b = new THREE.Mesh(new THREE.BoxGeometry(w,h,d), m);
    b.position.set(x,y,z); shuttle.add(b); return b; };
  for (let i=0;i<2;i++){
    add(3.6, 1.2, 1.4, WHITE, -i*4.2, 0, 0);
    add(3.62, .4, 1.42, GLASSM, -i*4.2, .1, 0);
  }
  shuttle.position.set(-430, 14.2, -205);
  scene.add(shuttle);
}

/* ---- ferry port: pier off the south avenue - the way on and off the island ---- */
const ferry = new THREE.Group();
{
  const deckMat = std({ color: 0xf2f0e9 });
  const bollMat = std({ color: 0x4a5462 });
  const add = (parent, geo, m, x, y, z, cs = true) => { const b = new THREE.Mesh(geo, m);
    b.position.set(x, y, z); b.castShadow = cs; b.receiveShadow = true; parent.add(b); return b; };

  // paved approach from the end of the south avenue, across the beach
  add(city, new THREE.BoxGeometry(8, .26, 24), PAVE, 27, .03, 150, false);
  // pier deck on pylons
  add(city, new THREE.BoxGeometry(9, .5, 58), deckMat, 27, .95, 188);
  for (let pz = 162; pz <= 214; pz += 8.6)
    for (const sx of [23.4, 30.6])
      add(city, new THREE.BoxGeometry(.7, 2.8, .7), deckMat, sx, -.3, pz);
  // railing on the west edge, bollards on the berth side
  add(city, new THREE.BoxGeometry(.16, .1, 56), WHITE, 22.9, 2.2, 188, false);
  for (let pz = 162; pz <= 214; pz += 5.6)
    add(city, new THREE.BoxGeometry(.12, 1.05, .12), WHITE, 22.9, 1.72, pz, false);
  for (let pz = 168; pz <= 212; pz += 11)
    add(city, new THREE.CylinderGeometry(.3, .36, .7, 10), bollMat, 31, 1.5, pz);
  // gangway resting on the pier edge at the berth
  const gang = add(city, new THREE.BoxGeometry(2.2, .14, 4.2), WHITE, 32.4, 1.28, 192);
  gang.rotation.z = -.18;

  // ferry terminal hut on the shore end
  {
    const hut = new THREE.Group(); hut.position.set(16.5, 0, 157); city.add(hut);
    add(hut, new THREE.BoxGeometry(10, .4, 7.6), PAVE, 0, .2, 0, false);
    add(hut, new THREE.BoxGeometry(8.6, 3.4, 6), WHITE, 0, 2.1, 0);
    add(hut, new THREE.BoxGeometry(8.66, 1.1, 6.06), GLASSM, 0, 2.2, 0, false);
    add(hut, new THREE.BoxGeometry(9.7, .2, 7.1), BAND, 0, 3.86, 0, false);
    add(hut, new THREE.BoxGeometry(9.6, .35, 7), WHITE, 0, 4.1, 0);
    const cv = document.createElement('canvas'); cv.width = 256; cv.height = 64;
    const x2 = cv.getContext('2d');
    x2.fillStyle = '#ffffff'; x2.fillRect(0, 0, 256, 64);
    x2.fillStyle = '#11bf9d'; x2.font = '700 30px "Avenir Next", "Segoe UI", sans-serif';
    x2.textAlign = 'center'; x2.textBaseline = 'middle'; x2.fillText('FERRY', 128, 34);
    const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace;
    const sign = new THREE.Mesh(new THREE.PlaneGeometry(4.6, 1.15), new THREE.MeshBasicMaterial({ map: t }));
    sign.position.set(0, 3.05, 3.08); hut.add(sign);
  }

  // channel buoys with mint beacons
  const buoys = [];
  for (const [bx, bz] of [[48, 214], [12, 236], [40, 256]]){
    const b = new THREE.Group();
    add(b, new THREE.CylinderGeometry(.65, .85, 1.1, 10), WHITE, 0, 0, 0);
    add(b, new THREE.CylinderGeometry(.08, .08, 1.3, 6), WHITE, 0, 1.1, 0, false);
    add(b, new THREE.SphereGeometry(.18, 8, 8), GLOW, 0, 1.8, 0, false);
    b.userData.dynamic = true; // bobs on the swell - excluded from the static bake
    b.position.set(bx, -.55, bz); city.add(b); buoys.push(b);
  }

  // the ferry - double-ended (twin bows), so it shuttles without turning
  const fad = (w, h, d, m, x, y, z, cs = true) => add(ferry, new THREE.BoxGeometry(w, h, d), m, x, y, z, cs);
  fad(7, 2.3, 19, WHITE, 0, .45, 0);                         // hull
  for (const sz of [1, -1]){
    const bow = add(ferry, new THREE.CylinderGeometry(4.6, 4.94, 2.3, 4), WHITE, 0, .45, sz*9.4);
    bow.rotation.y = Math.PI/4;                              // vertex points fore/aft
  }
  fad(7.14, .3, 19.2, BAND, 0, -.35, 0, false);              // mint waterline
  fad(6.2, 2.0, 13, WHITE, 0, 2.55, 0);                      // main cabin
  fad(6.26, .8, 13.06, GLASSM, 0, 2.75, 0, false);           // window run
  fad(6.5, .3, 14, WHITE, 0, 3.7, 0);                        // promenade roof
  fad(4.6, 1.7, 6.5, WHITE, 0, 4.7, 0);                      // upper saloon
  fad(4.66, .7, 6.56, GLASSM, 0, 4.8, 0, false);
  fad(4.9, .25, 7, WHITE, 0, 5.66, 0);
  add(ferry, new THREE.CylinderGeometry(.55, .75, 2.0, 12), WHITE, 0, 6.5, 0);  // funnel
  add(ferry, new THREE.CylinderGeometry(.58, .58, .4, 12), BAND, 0, 7.2, 0, false);
  ferry.position.set(35.9, -.8, 196);
  scene.add(ferry);

  // sailing schedule: berth -> cross the bay -> hold off-shore -> return
  const FB = { z0: 196, z1: 600, t: 0, phase: 'berth' };
  const dur = { berth: 11, out: 34, hold: 6, back: 34 };
  const ease = k => k*k*(3 - 2*k);
  tick((dt, t) => {
    FB.t += dt;
    if (FB.phase === 'berth'){ if (FB.t > dur.berth){ FB.phase = 'out'; FB.t = 0; } }
    else if (FB.phase === 'out'){
      const k = Math.min(1, FB.t/dur.out);
      ferry.position.z = FB.z0 + ease(k)*(FB.z1 - FB.z0);
      if (k >= 1){ FB.phase = 'hold'; FB.t = 0; }
    }
    else if (FB.phase === 'hold'){ if (FB.t > dur.hold){ FB.phase = 'back'; FB.t = 0; } }
    else {
      const k = Math.min(1, FB.t/dur.back);
      ferry.position.z = FB.z1 - ease(k)*(FB.z1 - FB.z0);
      if (k >= 1){ FB.phase = 'berth'; FB.t = 0; }
    }
    ferry.position.y = -.8 + Math.sin(t*1.25)*.05;           // gentle swell
    ferry.rotation.z = Math.sin(t*.85)*.012;
    ferry.rotation.x = Math.sin(t*1.6)*.006;
    buoys.forEach((b, i) => {
      b.position.y = -.55 + Math.sin(t*1.4 + i*2.1)*.12;
      b.rotation.x = Math.sin(t*.9 + i)*.06;
    });
  });
}

/* ---- drifting clouds (cast soft shadows over the city) ---- */
const clouds = [];
for (const [x, y, z, s] of [[-60, 62, -40, 2.6], [76, 68, 36, 2], [-124, 74, 12, 3.1], [16, 82, -96, 2.3]]){
  const g = new THREE.Group();
  const cm = std({ color: 0xffffff });
  for (let i=0;i<4;i++){
    const b = new THREE.Mesh(new THREE.IcosahedronGeometry(2.2 - i*.35, 0), cm);
    b.position.set(i*2.2 - 3.3, (i%2)*.7, (i%3)*.8 - .8);
    b.castShadow = true; g.add(b);
  }
  g.position.set(x, y, z); g.scale.setScalar(s);
  scene.add(g); clouds.push(g);
}

/* ---- static-geometry bake: collapse the whole built city into a handful of
   merged meshes (one per material) so draw calls stay flat as the city grows.
   Skipped: animated objects (userData.dynamic), shader water, textured signs. ---- */
{
  city.updateMatrixWorld(true);
  const isDyn = o => { for (let p = o; p; p = p.parent){ if (p.userData && p.userData.dynamic) return true; } return false; };
  const buckets = new Map(), baked = [];
  city.traverse(o => {
    if (!o.isMesh || o.isInstancedMesh) return;
    const m = o.material;
    if (Array.isArray(m) || m.isShaderMaterial || m.map || isDyn(o)) return;
    const key = m.uuid + (o.castShadow ? 'c' : '') + (o.receiveShadow ? 'r' : '');
    if (!buckets.has(key)) buckets.set(key, { mat: m, cast: o.castShadow, recv: o.receiveShadow, geos: [] });
    let ge = o.geometry.clone().applyMatrix4(o.matrixWorld);
    if (ge.index) ge = ge.toNonIndexed(); // mergeGeometries needs uniform indexing
    buckets.get(key).geos.push(ge);
    baked.push(o);
  });
  for (const o of baked) o.removeFromParent();
  for (const { mat, cast, recv, geos } of buckets.values()){
    const merged = mergeGeometries(geos, false);
    if (!merged) continue;
    const mesh = new THREE.Mesh(merged, mat);
    mesh.castShadow = cast; mesh.receiveShadow = recv;
    scene.add(mesh);
  }
}

/* ============================================================ */
/* 6. ROAD GRAPH + PATHING                                      */
/* ============================================================ */
// the four extreme grid corners sit out in the water - no junction there
const validNode = (i, j) => Math.hypot(L[i], L[j]) < ISLAND_R;

function bfsPath(si, sj, goals){
  const key = (i,j) => i*NL+j;
  if (!validNode(si, sj)) return null;
  const prev = new Array(NL*NL).fill(-2), q = [[si,sj]];
  prev[key(si,sj)] = -1;
  const goalSet = new Set(goals.map(g => key(g[0], g[1])));
  let found = null;
  while (q.length){
    const [i,j] = q.shift();
    if (goalSet.has(key(i,j))){ found = [i,j]; break; }
    for (const [di,dj] of [[1,0],[-1,0],[0,1],[0,-1]]){
      const ni = i+di, nj = j+dj;
      if (ni<0||ni>=NL||nj<0||nj>=NL || !validNode(ni, nj)) continue;
      if (prev[key(ni,nj)] === -2){ prev[key(ni,nj)] = key(i,j); q.push([ni,nj]); }
    }
  }
  if (!found) return null;
  const path = []; let k = key(found[0], found[1]);
  while (k !== -1){ path.unshift([Math.floor(k/NL), k%NL]); k = prev[k]; }
  return path;
}

// candidate start nodes for a point sitting on/near the road network
function startNodes(p){
  let best = null;
  for (const c of L){
    const dx = Math.abs(p.x - c), dz = Math.abs(p.z - c);
    if (!best || dx < best.d) best = { d: dx, axis: 'x', c };
    if (!best || dz < best.d) best = { d: dz, axis: 'z', c };
  }
  const ci = L.indexOf(best.c);
  const other = best.axis === 'x' ? p.z : p.x;
  let lo = 0; while (lo < NL-1 && L[lo+1] <= other) lo++;
  const hi = Math.min(NL-1, lo+1);
  return best.axis === 'x'
    ? [[ci, lo], [ci, hi]]
    : [[lo, ci], [hi, ci]];
}

function pathToWorld(nodePath){ return nodePath.map(([i,j]) => new THREE.Vector3(L[i], 0, L[j])); }

function routeTo(fromPos, goals){
  let best = null;
  for (const [si,sj] of startNodes(fromPos)){
    const np = bfsPath(si, sj, goals);
    if (!np) continue;
    const w = pathToWorld(np);
    const cost = fromPos.distanceTo(w[0]) + (w.length-1)*54;
    if (!best || cost < best.cost) best = { cost, pts: w };
  }
  const pts = best ? best.pts : [];
  // drop the first node if walking to it would mean doubling back
  if (pts.length > 1 && fromPos.distanceTo(pts[1]) < pts[0].distanceTo(pts[1])) pts.shift();
  return [fromPos.clone(), ...pts];
}

const PLAZA_CORNERS = [[2,2],[3,2],[2,3],[3,3]]; // the four junctions around the HQ block
function nodeNearest(pos){
  let best = null;
  for (let i=0;i<NL;i++) for (let j=0;j<NL;j++){
    if (!validNode(i, j)) continue;
    const d = (L[i]-pos.x)**2 + (L[j]-pos.z)**2;
    if (!best || d < best.d) best = { d, n: [i,j] };
  }
  return best.n;
}

function snapToRoad(p){
  let best = null;
  for (const c of L){
    const ext = ROAD_EXT(c);
    const c1 = new THREE.Vector3(c, 0, THREE.MathUtils.clamp(p.z, -ext, ext));
    const c2 = new THREE.Vector3(THREE.MathUtils.clamp(p.x, -ext, ext), 0, c);
    for (const cand of [c1, c2]){
      const d = cand.distanceTo(p);
      if (!best || d < best.d) best = { d, cand };
    }
  }
  const s = best.cand;
  if (s.length() < 30) s.multiplyScalar(30 / Math.max(s.length(), .001)); // keep clear of the shield
  return s;
}

// ground height for walkers (HQ plaza deck + south exit ramp)
function groundY(x, z){
  if (Math.abs(x) < 4.6 && z >= 22 && z < 24.2) return .8 * (1 - (z - 22)/2.2);
  if (Math.abs(x) < 23 && z > -18 && z < 22) return .8;
  return 0;
}

// shared waypoint walker: advances e.pos along e.path, returns true when done
function walkPath(e, speed, dt){
  let remaining = speed * dt;
  while (remaining > 0 && e.wp < e.path.length){
    const target = e.path[e.wp];
    const dx = target.x - e.pos.x, dz = target.z - e.pos.z;
    const len = Math.hypot(dx, dz);
    if (len < .05){ e.wp++; continue; }
    const step = Math.min(len, remaining);
    e.pos.x += dx/len*step; e.pos.z += dz/len*step;
    e.heading = yawFromDir(dx/len, dz/len);
    remaining -= step;
  }
  return e.wp >= e.path.length;
}

/* ============================================================ */
/* 7. AMBIENT LIFE - traffic, pedestrians                       */
/* ============================================================ */
// the traffic fleet (models/civilian-car.html) - four bodies, one formula, front toward +x
function makeCivCar(style){
  const g = new THREE.Group();
  const box = (w, h, d, m, x = 0, y = 0, z = 0, s = true) => {
    const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
    b.position.set(x, y, z); b.castShadow = s; b.receiveShadow = true;
    g.add(b); return b;
  };
  const cyl = (rt, rb, h, seg, m, x, y, z) => {
    const c = new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), m);
    c.position.set(x, y, z); c.castShadow = true; c.receiveShadow = true;
    g.add(c); return c;
  };
  const wedge = (xb, yb, xt, yt, w, cx, cz) => {
    const wl = Math.hypot(xt - xb, yt - yb), ang = Math.atan2(yt - yb, xt - xb);
    const gl = box(wl, .22, w, GLASSM, cx + (xb + xt)/2, (yb + yt)/2, cz);
    gl.rotation.z = ang;
    let nx = -Math.sin(ang), ny = Math.cos(ang);
    if (ny < 0){ nx = -nx; ny = -ny; }
    gl.position.x -= nx * .075; gl.position.y -= ny * .075;
    return gl;
  };
  const wheel = (x, z) => {
    const t = cyl(.25, .25, .16, 8, TIRE, x, .25, z);  t.rotation.x = Math.PI/2;
    const r = cyl(.165, .165, .18, 8, WHITE, x, .25, z); r.rotation.x = Math.PI/2;
    const h = cyl(.08, .08, .2, 8, HUB, x, .25, z);    h.rotation.x = Math.PI/2;
  };
  const car = o => {
    const BODY = std({ color: o.color, roughness: .85 });
    const W = 1.24, F = o.L/2, x = 0, z = 0;
    const gh = o.gh;
    box(o.L, .5, W, BODY, x, .55, z);
    const cLen = o.cabF - o.cabR, cMid = x + (o.cabF + o.cabR)/2;
    box(cLen, gh, W - .18, GLASSM, cMid, .8 + gh/2, z);
    box(cLen + .08, .09, W - .1, BODY, cMid, .8 + gh + .045, z);
    wedge(o.cabF + o.wsRun, .82, o.cabF, .8 + gh, W - .2, x, z);
    wedge(o.cabR - o.rearRun, .82, o.cabR, .8 + gh, W - .2, x, z);
    for (const mx of o.mullions)
      box(.07, gh + .04, W - .14, WHITE, x + mx, .8 + gh/2, z, false);
    if (o.panelFrom !== undefined)
      box(o.panelFrom - o.cabR + .05, gh + .06, W - .14, BODY,
        x + (o.cabR + o.panelFrom)/2, .8 + gh/2, z, false);
    if (o.rails) for (const rz of [.36, -.36])
      box(cLen * .8, .06, .07, WHITE, cMid, .8 + gh + .13, z + rz);
    for (const bx of [F - .04, -F + .04]) box(.26, .2, W + .08, WHITE, x + bx, .38, z);
    box(.04, .09, .56, HEAD, x + F + .1, .37, z, false);
    for (const sz of [1, -1]){
      box(o.L - 1.1, .06, .03, WHITE, x, .315, z + (W/2 + .01)*sz, false);
      box(o.L - 1.1, .03, .03, MINTLN, x, .365, z + (W/2 + .01)*sz, false);
    }
    for (const sz of [1, -1]){
      box(.05, .11, .26, HEAD, x + F + .01, .67, z + .37*sz, false);
      box(.04, .12, .2, o.tail, x - F - .01, .64, z + .4*sz, false);
      box(.1, .04, .04, WHITE, x + o.cabF + .04, .9, z + (W/2 - .03)*sz, false);
      box(.07, .11, .06, WHITE, x + o.cabF + .07, .9, z + (W/2 + .07)*sz, false);
      for (const hx of o.handles)
        box(.15, .045, .03, WHITE, x + hx, .72, z + (W/2 + .01)*sz, false);
    }
    wheel(x + F - .5, z + .52); wheel(x + F - .5, z - .52);
    wheel(x - F + .5, z + .52); wheel(x - F + .5, z - .52);
  };
  const styles = [
    { color: 0xf2a24e, L: 2.7, gh: .36,    // orange van
      cabF: .55, cabR: -1.2, wsRun: .36, rearRun: .15,
      mullions: [-.2], panelFrom: -.6, handles: [.15, -.45], tail: MINTTL },
    { color: 0x55917a, L: 2.85, gh: .34,   // green SUV
      cabF: .5, cabR: -1.25, wsRun: .36, rearRun: .16,
      mullions: [-.18, -.78], rails: true, handles: [.1, -.5], tail: REDTL },
    { color: 0x5570d6, L: 2.6, gh: .32,    // blue sedan
      cabF: .45, cabR: -1.0, wsRun: .34, rearRun: .3,
      mullions: [-.26], handles: [.08, -.52], tail: MINTTL },
    { color: 0xf05a54, L: 2.25, gh: .3,    // red hatchback
      cabF: .4, cabR: -.92, wsRun: .32, rearRun: .18,
      mullions: [-.24], handles: [-.05], tail: MINTTL },
  ];
  car(styles[style % styles.length]);
  bakeRigidGroup(g);
  return g;
}

const traffic = [];
{
  const lanes = [
    { loop: makeLoop(29, 5),  dir: 1,  speed: 11,   n: 5 },
    { loop: makeLoop(25, 5),  dir: -1, speed: 10,   n: 4 },
    { loop: makeLoop(83, 6),  dir: 1,  speed: 12.5, n: 7 },
    { loop: makeLoop(79, 6),  dir: -1, speed: 11.5, n: 6 },
  ];
  let ci = 0;
  for (const lane of lanes){
    for (let i=0;i<lane.n;i++){
      const g = makeCivCar(ci++);
      scene.add(g);
      traffic.push({ g, lane, d: (lane.loop.len/lane.n)*i + rng()*6 });
    }
  }
  // parked cars fill the block parking strips
  for (const s of PARK_SPOTS){
    const g = makeCivCar(ci++);
    g.position.set(s.x, .13, s.z);
    g.rotation.y = s.ry;
    scene.add(g);
  }
}

const bubbleTex = {};
{
  const make = (draw) => {
    const cv = document.createElement('canvas'); cv.width = 128; cv.height = 104;
    const x = cv.getContext('2d');
    x.fillStyle = '#ffffff';
    x.strokeStyle = '#d8dcea'; x.lineWidth = 4;
    const r = 24;
    x.beginPath(); x.roundRect(8, 8, 112, 66, r); x.fill(); x.stroke();
    x.beginPath(); x.moveTo(52, 72); x.lineTo(64, 96); x.lineTo(76, 72); x.closePath(); x.fill();
    draw(x);
    const t = new THREE.CanvasTexture(cv); t.colorSpace = THREE.SRGBColorSpace;
    return t;
  };
  bubbleTex.dots = make(x => {
    x.fillStyle = '#7d88a8';
    for (const dx of [-26, 0, 26]){ x.beginPath(); x.arc(64+dx, 41, 7, 0, 7); x.fill(); }
  });
  bubbleTex.alert = make(x => {
    x.fillStyle = '#e6493f';
    x.fillRect(58, 20, 12, 30); x.beginPath(); x.arc(64, 62, 7, 0, 7); x.fill();
  });
}
const bubbleMatDots = new THREE.SpriteMaterial({ map: bubbleTex.dots, transparent: true });
const bubbleMatAlert = new THREE.SpriteMaterial({ map: bubbleTex.alert, transparent: true });

const P_MAT = {
  mint: std({ color: 0x52e7c3, roughness: .55 }),
  skin: std({ color: PAL.skin }),
  hair: std({ color: 0x5d6470 }),
  gray: std({ color: 0xc4cad4 }),
  dark: std({ color: 0x4a5462 }),
};
const PERSON_KINDS = ['hoodie', 'engineer', 'cap', 'woman', 'office'];
function makePerson(kind = pick(PERSON_KINDS)){
  // voxel citizens (~1.55 tall, feet at y 0, facing +z) - five outfits:
  // hoodie kid, hard-hat engineer, cap courier, office lady, tie clerk.
  // Boxes are collected per material and merged so each person costs ~6 draws.
  const M = P_MAT, parts = [];
  const part = (w, h, d, m, x, y, z) => parts.push([w, h, d, m, x, y, z]);
  const mirror = (w, h, d, m, x, y, z) => { part(w, h, d, m, x, y, z); part(w, h, d, m, -x, y, z); };

  part(.4, .38, .36, M.skin, 0, 1.26, 0);              // head
  mirror(.055, .075, .03, M.dark, .09, 1.31, .17);     // eyes

  const torso = (m = WHITE) => part(.54, .5, .32, m, 0, .85, 0);
  const longArms = (cuffM, ax = .34) => {
    mirror(.14, .42, .16, WHITE, ax, .87, 0);
    if (cuffM) mirror(.15, .05, .17, cuffM, ax, .68, 0);
    mirror(.11, .09, .12, M.skin, ax, .61, 0);
  };
  const shortArms = (bandM) => {
    mirror(.15, .22, .17, WHITE, .34, .97, 0);
    if (bandM) mirror(.155, .05, .175, bandM, .34, .87, 0);
    mirror(.11, .26, .12, M.skin, .34, .71, 0);
  };
  const legs = m => mirror(.17, .5, .2, m, .12, .35, 0);
  const shoes = (m, soleM) => {
    if (soleM){
      mirror(.19, .05, .28, soleM, .12, .025, .02);
      mirror(.18, .1, .27, m, .12, .1, .02);
    } else mirror(.18, .14, .27, m, .12, .07, .02);
  };
  const flatHair = () => {
    part(.44, .15, .42, M.hair, 0, 1.49, -.01);
    part(.44, .16, .09, M.hair, 0, 1.36, -.185);
  };

  if (kind === 'hoodie'){
    flatHair();
    torso();
    part(.3, .05, .06, M.mint, 0, 1.07, .14);          // hood trim
    mirror(.035, .16, .03, M.mint, .07, .96, .16);     // drawstrings
    longArms(M.mint);
    legs(M.gray);
    shoes(WHITE, M.mint);
  } else if (kind === 'engineer'){
    part(.41, .1, .37, M.hair, 0, 1.43, 0);            // hair under the helmet
    part(.46, .15, .42, WHITE, 0, 1.53, 0);            // hard hat crown
    part(.52, .05, .46, WHITE, 0, 1.455, .02);         // hat brim
    part(.12, .07, .04, M.mint, 0, 1.53, .2);          // hat badge
    torso();
    part(.08, .3, .05, M.mint, 0, .93, .15);           // tie
    mirror(.1, .05, .04, M.mint, .14, .99, .15);       // pocket flaps
    longArms(M.mint);
    legs(WHITE);
    mirror(.13, .13, .03, M.gray, .12, .38, .095);     // knee patches
    mirror(.18, .06, .21, M.mint, .12, .13, 0);        // ankle bands
    shoes(WHITE);
    mirror(.19, .06, .1, M.dark, .12, .08, .12);       // boot toes
  } else if (kind === 'cap'){
    part(.42, .1, .38, M.hair, 0, 1.44, -.01);
    part(.42, .12, .08, M.hair, 0, 1.38, -.18);
    part(.44, .13, .4, WHITE, 0, 1.53, -.01);          // cap crown
    part(.36, .045, .26, M.mint, 0, 1.485, .26);       // cap brim
    torso();
    part(.42, .06, .34, M.mint, 0, 1.085, 0);          // polo collar
    shortArms(M.mint);
    part(.55, .07, .34, M.gray, 0, .625, 0);           // belt
    part(.1, .07, .04, M.mint, 0, .625, .16);          // buckle
    legs(WHITE);
    mirror(.04, .12, .14, M.mint, .215, .42, .02);     // cargo pocket flaps
    mirror(.18, .07, .21, M.gray, .12, .135, 0);       // pant hems
    shoes(WHITE, M.mint);
  } else if (kind === 'woman'){
    part(.46, .16, .42, M.hair, 0, 1.49, -.01);        // bob: top
    mirror(.06, .36, .4, M.hair, .2, 1.25, -.03);      // bob: sides
    part(.44, .38, .09, M.hair, 0, 1.24, -.185);       // bob: back
    part(.44, .07, .05, M.hair, 0, 1.45, .17);         // bob: fringe
    part(.5, .42, .3, WHITE, 0, .92, 0);               // bodice
    part(.56, .38, .34, WHITE, 0, .56, 0);             // skirt
    part(.57, .05, .35, M.mint, 0, .755, 0);           // belt
    part(.18, .06, .05, M.mint, 0, 1.1, .14);          // collar
    longArms(M.mint, .32);
    mirror(.14, .3, .16, M.skin, .11, .23, 0);         // bare legs
    mirror(.16, .1, .24, M.mint, .11, .05, .02);       // mint shoes
  } else { // office
    flatHair();
    torso();
    part(.09, .32, .05, M.mint, 0, .92, .15);          // tie
    shortArms(M.mint);
    part(.55, .06, .33, M.gray, 0, .625, 0);           // belt
    legs(M.gray);
    mirror(.18, .07, .21, M.dark, .12, .135, 0);       // pant cuffs
    shoes(M.dark);
  }

  const g = new THREE.Group();
  const buckets = new Map();
  for (const [w, h, d, m, x, y, z] of parts){
    const ge = new THREE.BoxGeometry(w, h, d);
    ge.translate(x, y, z);
    if (!buckets.has(m)) buckets.set(m, []);
    buckets.get(m).push(ge);
  }
  for (const [m, geos] of buckets){
    const mesh = new THREE.Mesh(mergeGeometries(geos), m);
    mesh.castShadow = mesh.receiveShadow = true;
    g.add(mesh);
  }
  const bub = new THREE.Sprite(bubbleMatDots);
  bub.position.y = 2.0; bub.scale.set(0,0,1); g.add(bub);
  g.userData.bub = bub;
  return g;
}

const pairs = [];
{
  // hand-placed on each plot's pavement (x, z, walking height, flee-home)
  const spots = [
    { x: 7.5,   z: 51,    y: .65,  home: [4, 60] },      // fountain park path
    { x: -10,   z: 57.5,  y: .65,  home: [-14, 60] },
    { x: 60,    z: -4,    y: 1.03, home: [58, -8] },     // halo radar campus plaza
    { x: 62,    z: 104,   y: .65,  home: [64, 101] },    // gas station forecourt (port road)
    { x: -4,    z: -48,   y: .65,  home: [0, -47.5] },   // retail hall storefront N
    { x: 54,    z: -50,   y: .7,   home: [57, -48] },    // spire duo podium NE
    { x: -35.5, z: -4,    y: .65,  home: [-37, 0] },     // mixed-use hall front W
    { x: 60,    z: 49,    y: .72,  home: [56, 45] },     // office park SE plaza
    { x: 0,     z: -113,  y: .7,   home: [4, -112] },    // spire duo podium far north
    { x: -49,   z: 55,    y: .12,  home: [-47, 58] },    // quad court walk SW
    { x: -.5,   z: 98.7,  y: .12,  home: [3, 97] },      // office court walk S
    { x: 103,   z: -57,   y: .12,  home: [100, -55] },   // twisted tower plaza
    { x: -1,    z: -37.6, y: .15,  home: [3, -37] },     // retail N parking strip
    { x: 27.8,  z: 168,   y: 1.2,  home: [26.5, 175] },  // ferry pier promenade
    { x: 2,     z: 138.2, y: 11.29,home: [5, 138.2] },   // Bayfront station platform
    { x: -8,    z: 8,     y: .8,   home: [-14, 18] },    // HQ plaza
    { x: 9,     z: 6,     y: .8,   home: [15, 18] },
  ];
  for (const s of spots){
    const ang = rng()*Math.PI*2;
    const off = new THREE.Vector3(Math.cos(ang)*.55, 0, Math.sin(ang)*.55);
    const ka = pick(PERSON_KINDS); // two different outfits per chatting pair
    const kb = PERSON_KINDS[(PERSON_KINDS.indexOf(ka) + 1 + Math.floor(rng()*4)) % PERSON_KINDS.length];
    const a = makePerson(ka), b = makePerson(kb);
    a.position.set(s.x+off.x, s.y, s.z+off.z);
    b.position.set(s.x-off.x, s.y, s.z-off.z);
    a.lookAt(b.position.x, s.y, b.position.z);
    b.lookAt(a.position.x, s.y, a.position.z);
    scene.add(a, b);
    pairs.push({
      a, b, y: s.y, talkT: rng()*2, who: 0, state: 'talk',
      homeA: a.position.clone(), homeB: b.position.clone(),
      flee: new THREE.Vector3(s.home[0], s.y, s.home[1]),
    });
  }
}

/* ============================================================ */
/* 8. FX - rings, dust, sparks, confetti, tracers, beams        */
/* ============================================================ */
const fx = [];
function addFx(obj){ fx.push(obj); }

function ringFx(pos, color = 0xbffbe8, maxR = 7, life = .6, y = .12){
  const m = new THREE.Mesh(new THREE.RingGeometry(.8, 1, 40),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: .95, side: THREE.DoubleSide }));
  m.rotation.x = -Math.PI/2; m.position.set(pos.x, y, pos.z);
  scene.add(m);
  addFx({ t: 0, life, update(dt){
    this.t += dt; const k = this.t/this.life;
    m.scale.setScalar(1 + k*maxR);
    m.material.opacity = .95*(1-k);
    if (k >= 1){ scene.remove(m); return false; } return true;
  }});
}

function puffFx(pos, n = 10, color = 0xefe9dd, spd = 7){
  const items = [];
  for (let i=0;i<n;i++){
    const m = new THREE.Mesh(new THREE.IcosahedronGeometry(.28+Math.random()*.2, 0),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: .9 }));
    m.position.copy(pos);
    const a = Math.random()*Math.PI*2;
    items.push({ m, v: new THREE.Vector3(Math.cos(a)*spd*(.4+Math.random()*.6), 2+Math.random()*4, Math.sin(a)*spd*(.4+Math.random()*.6)) });
    scene.add(m);
  }
  addFx({ t: 0, life: .8, update(dt){
    this.t += dt; const k = this.t/this.life;
    for (const it of items){
      it.v.y -= 9*dt;
      it.m.position.addScaledVector(it.v, dt);
      it.m.material.opacity = .9*(1-k);
      it.m.scale.setScalar(1-k*.5);
    }
    if (k >= 1){ items.forEach(it => scene.remove(it.m)); return false; } return true;
  }});
}

function sparkFx(pos, color = 0xfff3a0){
  const items = [];
  for (let i=0;i<6;i++){
    const m = new THREE.Mesh(new THREE.SphereGeometry(.11, 6, 6),
      new THREE.MeshBasicMaterial({ color }));
    m.position.copy(pos);
    const v = new THREE.Vector3(Math.random()-.5, Math.random()-.2, Math.random()-.5).normalize().multiplyScalar(7);
    items.push({ m, v }); scene.add(m);
  }
  addFx({ t: 0, life: .28, update(dt){
    this.t += dt; const k = this.t/this.life;
    for (const it of items){ it.m.position.addScaledVector(it.v, dt); it.m.scale.setScalar(1-k); }
    if (k >= 1){ items.forEach(it => scene.remove(it.m)); return false; } return true;
  }});
}

function explosionFx(pos, scale = 1, color = 0xff8e86){
  const origin = pos.clone();
  ringFx(origin, 0xfff3a0, 4.4*scale, .38, origin.y);
  ringFx(origin, color, 7.5*scale, .58, origin.y);
  puffFx(origin, Math.round(14*scale), 0xe7dfd2, 10*scale);
  sparkFx(origin, 0xfff3a0);
  const flash = new THREE.Mesh(new THREE.SphereGeometry(.75*scale, 16, 12),
    new THREE.MeshBasicMaterial({ color: 0xfff3a0, transparent: true, opacity: .95, depthWrite: false }));
  flash.position.copy(origin); flash.renderOrder = 1000; scene.add(flash);

  const debris = [];
  for (let i=0;i<Math.round(12 + scale*10);i++){
    const m = new THREE.Mesh(new THREE.BoxGeometry(.16*scale, .1*scale, .2*scale),
      new THREE.MeshBasicMaterial({ color: i % 3 ? color : 0xffffff, transparent: true, opacity: .95 }));
    m.position.copy(origin);
    const v = new THREE.Vector3(Math.random()-.5, Math.random()*.8+.2, Math.random()-.5)
      .normalize().multiplyScalar((6 + Math.random()*8) * scale);
    debris.push({ m, v, r: new THREE.Vector3(Math.random()*8, Math.random()*8, Math.random()*8) });
    scene.add(m);
  }

  addFx({ t: 0, life: .9, update(dt){
    this.t += dt; const k = this.t/this.life;
    flash.scale.setScalar(1 + k*5.5);
    flash.material.opacity = .95*(1-k);
    for (const it of debris){
      it.v.y -= 11*dt;
      it.m.position.addScaledVector(it.v, dt);
      it.m.rotation.x += it.r.x*dt; it.m.rotation.y += it.r.y*dt; it.m.rotation.z += it.r.z*dt;
      it.m.material.opacity = .95*(1-k);
    }
    if (k >= 1){ scene.remove(flash); debris.forEach(it => scene.remove(it.m)); return false; }
    return true;
  }});
}

function confettiFx(pos){
  const colors = [0x7df5d4, 0xff9d96, 0xc9aef5, 0xffe28a, 0xaecbfa, 0xffffff];
  const items = [];
  for (let i=0;i<70;i++){
    const m = new THREE.Mesh(new THREE.PlaneGeometry(.5, .5),
      new THREE.MeshBasicMaterial({ color: colors[i % colors.length], side: THREE.DoubleSide, transparent: true }));
    m.position.copy(pos);
    const v = new THREE.Vector3(Math.random()-.5, Math.random()*.8+.4, Math.random()-.5).normalize()
      .multiplyScalar(7 + Math.random()*11);
    items.push({ m, v, r: new THREE.Vector3(Math.random()*6, Math.random()*6, Math.random()*6) });
    scene.add(m);
  }
  addFx({ t: 0, life: 1.7, update(dt){
    this.t += dt; const k = this.t/this.life;
    for (const it of items){
      it.v.y -= 11*dt;
      it.m.position.addScaledVector(it.v, dt);
      it.m.rotation.x += it.r.x*dt; it.m.rotation.y += it.r.y*dt; it.m.rotation.z += it.r.z*dt;
      it.m.material.opacity = k > .75 ? (1-k)/.25 : 1;
    }
    if (k >= 1){ items.forEach(it => scene.remove(it.m)); return false; } return true;
  }});
}

const tracers = [];
const fxColor = mat => mat && mat.color ? mat.color.getHex() : 0x7df5d4;
function additiveMat(color, opacity){
  return new THREE.MeshBasicMaterial({
    color, transparent: true, opacity,
    blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false,
  });
}

function flashFx(pos, color = 0x7df5d4, scale = 1){
  const m = new THREE.Mesh(new THREE.SphereGeometry(.45*scale, 12, 8), additiveMat(color, .7));
  m.position.copy(pos); m.renderOrder = 1001; scene.add(m);
  ringFx(pos, color, 2.2*scale, .22, pos.y);
  addFx({ t: 0, life: .24, update(dt){
    this.t += dt; const k = this.t/this.life;
    m.scale.setScalar(1 + k*3.4);
    m.material.opacity = .7*(1-k);
    if (k >= 1){ scene.remove(m); return false; }
    return true;
  }});
}

function impactBurstFx(pos, color = 0x7df5d4, scale = 1){
  flashFx(pos, color, scale);
  sparkFx(pos, color);
  ringFx(pos, color, 3.4*scale, .28, pos.y);
}

function streakFx(from, to, color = 0x7df5d4, life = .3, radius = .06, opacity = .48){
  const dir = to.clone().sub(from);
  const len = dir.length();
  if (len < .01) return;
  dir.normalize();
  const core = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, 1, 10), additiveMat(color, opacity));
  const glow = new THREE.Mesh(new THREE.CylinderGeometry(radius*3.4, radius*3.4, 1, 12), additiveMat(color, opacity*.22));
  for (const m of [core, glow]){
    m.position.copy(from).lerp(to, .5);
    m.scale.y = len;
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    m.renderOrder = 1000;
    scene.add(m);
  }
  addFx({ t: 0, life, update(dt){
    this.t += dt; const k = this.t/this.life;
    core.material.opacity = opacity*(1-k);
    glow.material.opacity = opacity*.22*(1-k);
    core.scale.x = core.scale.z = 1 + k*.45;
    glow.scale.x = glow.scale.z = 1 + k*.75;
    if (k >= 1){ scene.remove(core, glow); return false; }
    return true;
  }});
}

function pulseDotsFx(from, to, color = 0xbfffe9, count = 4, scale = 1){
  const dir = to.clone().sub(from);
  const len = dir.length();
  if (len < .01) return;
  const side = new THREE.Vector3(-dir.z, 0, dir.x);
  if (side.lengthSq() > .01) side.normalize();
  const dots = [];
  for (let i=0;i<count;i++){
    const f = (i + 1) / (count + 1);
    const m = new THREE.Mesh(new THREE.SphereGeometry(.18*scale, 10, 8), additiveMat(color, .56));
    m.position.copy(from).lerp(to, f);
    m.position.addScaledVector(side, (rng()-.5)*.9*scale);
    m.renderOrder = 1001;
    scene.add(m);
    dots.push({ m, f });
  }
  addFx({ t: 0, life: .34, update(dt){
    this.t += dt; const k = this.t/this.life;
    for (const d of dots){
      d.m.position.lerp(to, dt*(2.2 + d.f*2));
      d.m.scale.setScalar(1 + k*2.8);
      d.m.material.opacity = .56*(1-k);
    }
    if (k >= 1){ dots.forEach(d => scene.remove(d.m)); return false; }
    return true;
  }});
}

function fireTracer(from, dmg, mat, o = {}){
  const color = fxColor(mat);
  const g = new THREE.Group();
  const core = new THREE.Mesh(new THREE.SphereGeometry(.38, 12, 8), mat);
  core.scale.set(1, 1, 3.8);
  const glow = new THREE.Mesh(new THREE.SphereGeometry(.58, 12, 8), additiveMat(color, .38));
  glow.scale.set(1.55, 1.55, 5.8);
  const tail = new THREE.Mesh(new THREE.SphereGeometry(.46, 10, 8), additiveMat(color, .24));
  tail.position.z = 1.1;
  tail.scale.set(1.1, 1.1, 4.4);
  g.add(tail, glow, core);
  g.position.copy(from); scene.add(g);
  flashFx(from, color, .82);
  const target = monsterChest();
  if (target && o.streak !== false) streakFx(from, target, color, .26, .05, .42);
  tracers.push({ m: g, t: 0, dur: .24, from: from.clone(), dmg, color, source: o.source || 'tracer' });
  if (o.sound !== false) sfx.laser(mat === tracerBlueMat);
}

function fireLaserLine(from, dmg, color = 0x7df5d4, o = {}){
  const target = monsterChest();
  if (!target) return;
  const to = target.clone();
  const dir = to.clone().sub(from);
  const len = dir.length();
  if (len < .001) return;
  const mid = from.clone().lerp(to, .5);
  dir.normalize();
  const core = new THREE.Mesh(new THREE.CylinderGeometry(.026, .026, 1, 8), additiveMat(color, .86));
  const glow = new THREE.Mesh(new THREE.CylinderGeometry(.105, .105, 1, 10), additiveMat(color, .18));
  for (const beam of [glow, core]){
    beam.position.copy(mid);
    beam.scale.y = len;
    beam.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    scene.add(beam);
  }
  const cleanup = () => {
    scene.remove(core, glow);
    for (const beam of [core, glow]){
      beam.geometry.dispose();
      beam.material.dispose();
    }
  };
  flashFx(from, color, .58);
  if (o.sound !== false) sfx.laser(false);
  addFx({ t: 0, life: .16, hit: false, update(dt){
    this.t += dt;
    const k = Math.min(1, this.t / this.life);
    core.material.opacity = .86 * (1 - k);
    glow.material.opacity = .18 * (1 - k);
    if (!this.hit && k >= .72){
      this.hit = true;
      impactBurstFx(to, color, .52);
      damageMonster(dmg, to, from, o.source || 'drone-laser');
    }
    if (k >= 1){
      cleanup();
      return false;
    }
    return true;
  }});
}

function beamFx(from, getTarget, dur = .3, color = 0xaef7e4, scale = 1){
  const geo = new THREE.BufferGeometry().setFromPoints(new Array(9).fill(0).map(() => new THREE.Vector3()));
  const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color, transparent: true, opacity: 1, depthTest: false }));
  const core = new THREE.Mesh(new THREE.CylinderGeometry(.08*scale, .08*scale, 1, 10), additiveMat(color, .92));
  const glow = new THREE.Mesh(new THREE.CylinderGeometry(.34*scale, .34*scale, 1, 12), additiveMat(color, .18));
  scene.add(line, core, glow);
  addFx({ t: 0, life: dur, update(dt){
    this.t += dt; const k = this.t/this.life;
    const to = getTarget(); if (!to){ scene.remove(line, core, glow); return false; }
    const pts = [];
    for (let i=0;i<9;i++){
      const f = i/8;
      const p = from.clone().lerp(to, f);
      if (i>0 && i<8){
        p.x += (Math.random()-.5)*1.25*scale;
        p.y += (Math.random()-.5)*1.25*scale;
        p.z += (Math.random()-.5)*1.25*scale;
      }
      pts.push(p);
    }
    line.geometry.setFromPoints(pts);
    const mid = from.clone().lerp(to, .5);
    const dir = to.clone().sub(from);
    const len = dir.length();
    if (len > .001){
      dir.normalize();
      for (const beam of [core, glow]){
        beam.position.copy(mid);
        beam.scale.y = len;
        beam.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
      }
    }
    line.material.opacity = 1-k;
    core.material.opacity = .92*(1-k);
    glow.material.opacity = .18*(1-k);
    if (k >= 1){ scene.remove(line, core, glow); return false; } return true;
  }});
}

let shake = 0;
function addShake(s){ shake = Math.min(1.2, shake + s); }

let battleTrace = null;
const BATTLE_LOG_KEY = 'kachersoftCityHeroBattleLogs';
const BATTLE_LOG_LIMIT = 8;
const BATTLE_HP_MARKS = [240, 200, 160, 120, 80, 40, 0];
function traceVec(v){
  return v ? { x: +v.x.toFixed(2), y: +v.y.toFixed(2), z: +v.z.toFixed(2) } : null;
}
function traceViewport(){
  return {
    width: stage.clientWidth,
    height: stage.clientHeight,
    devicePixelRatio: +(window.devicePixelRatio || 1).toFixed(2),
    renderPixelRatio: +activeRenderPixelRatio.toFixed(2),
    breakpoint: activeCameraBreakpoint,
  };
}
function startBattleTrace(label = 'battle'){
  battleTrace = {
    label,
    startedAt: performance.now(),
    viewport: traceViewport(),
    events: [],
    frame: { count: 0, rawTotal: 0, scaledTotal: 0, rawMax: 0, scaledMax: 0 },
    damageBySource: {},
    hpMarks: [...BATTLE_HP_MARKS],
  };
  recordBattleEvent('traceStart', { camera: cameraSnapshot() });
  logBattleLine('traceStart', battleTrace.viewport);
  return battleTraceSnapshot();
}
function recordBattleEvent(type, data = {}){
  if (!battleTrace) return;
  const event = {
    type,
    gameTime: +(game.t || 0).toFixed(3),
    wallTime: +((performance.now() - battleTrace.startedAt) / 1000).toFixed(3),
    ...data,
  };
  battleTrace.events.push(event);
  if (type === 'spawn' || type === 'route' || type === 'monsterAttack' || type === 'kill' || type === 'removed')
    logBattleLine(type, event);
  if (type === 'kill') persistBattleTrace();
}
function recordBattleFrame(rawDt, scaledDt){
  if (!battleTrace) return;
  const frame = battleTrace.frame;
  frame.count += 1;
  frame.rawTotal += rawDt;
  frame.scaledTotal += scaledDt;
  frame.rawMax = Math.max(frame.rawMax, rawDt);
  frame.scaledMax = Math.max(frame.scaledMax, scaledDt);
}
function recordBattleDamage(source, amount, hpBefore, hpAfter, hitPos, sourcePos){
  if (!battleTrace) return;
  const key = source || 'unknown';
  battleTrace.damageBySource[key] = +(battleTrace.damageBySource[key] || 0) + amount;
  recordBattleEvent('damage', {
    source: key,
    amount,
    hpBefore: +hpBefore.toFixed(2),
    hpAfter: +hpAfter.toFixed(2),
    hit: traceVec(hitPos),
    sourcePos: traceVec(sourcePos),
  });
  while (battleTrace.hpMarks.length && hpAfter <= battleTrace.hpMarks[0]){
    const mark = battleTrace.hpMarks.shift();
    logBattleLine(`hp<=${mark}`, { source: key, amount, hpBefore: +hpBefore.toFixed(2), hpAfter: +hpAfter.toFixed(2) });
  }
}
function battleTraceSnapshot(){
  if (!battleTrace) return null;
  const frame = battleTrace.frame;
  return {
    ...battleTrace,
    frame: {
      count: frame.count,
      rawTotal: +frame.rawTotal.toFixed(3),
      scaledTotal: +frame.scaledTotal.toFixed(3),
      rawAvg: frame.count ? +(frame.rawTotal / frame.count).toFixed(4) : 0,
      scaledAvg: frame.count ? +(frame.scaledTotal / frame.count).toFixed(4) : 0,
      rawMax: +frame.rawMax.toFixed(4),
      scaledMax: +frame.scaledMax.toFixed(4),
    },
  };
}
function summarizeBattleTrace(trace = battleTraceSnapshot()){
  if (!trace) return null;
  const events = trace.events || [];
  const byType = type => events.find(e => e.type === type) || null;
  return {
    label: trace.label,
    viewport: trace.viewport,
    spawn: byType('spawn'),
    route: byType('route'),
    firstDamage: byType('damage'),
    firstAttack: byType('monsterAttack'),
    kill: byType('kill'),
    removed: byType('removed'),
    frame: trace.frame,
    damageBySource: trace.damageBySource,
  };
}
function logBattleLine(type, data = {}){
  const t = data.gameTime ?? +(game.t || 0).toFixed(3);
  const wall = data.wallTime ?? (battleTrace ? +((performance.now() - battleTrace.startedAt) / 1000).toFixed(3) : 0);
  console.log(`[CityHero Battle] ${type} game=${t}s wall=${wall}s`, data);
  if (type === 'kill' || type === 'removed') console.table(summarizeBattleTrace());
}
function storedBattleLogs(){
  try { return JSON.parse(localStorage.getItem(BATTLE_LOG_KEY) || '[]'); }
  catch(e){ return []; }
}
function persistBattleTrace(){
  const trace = battleTraceSnapshot();
  if (!trace) return null;
  const saved = { savedAt: new Date().toISOString(), summary: summarizeBattleTrace(trace), trace };
  try {
    const logs = storedBattleLogs();
    logs.unshift(saved);
    localStorage.setItem(BATTLE_LOG_KEY, JSON.stringify(logs.slice(0, BATTLE_LOG_LIMIT)));
  } catch(e){}
  return saved;
}
function battleLogText(){
  return JSON.stringify(storedBattleLogs(), null, 2);
}
function ensureBattleTraceForSpawn(){
  const ended = battleTrace && battleTrace.events.some(e => e.type === 'kill' || e.type === 'removed');
  if (!battleTrace || ended) startBattleTrace(`manual-${new Date().toISOString()}`);
}

function startCinematicFocus(pos, o = {}){
  if (simming) return;
  const priority = o.priority ?? 1;
  if (cinematic && cinematic.t < .45 && priority <= cinematic.priority) return;

  const focus = pos.clone();
  const viewDir = camera.position.clone().sub(orbit.target);
  if (viewDir.lengthSq() < 1) viewDir.set(-80, 62, 168);
  viewDir.normalize();

  const side = new THREE.Vector3(viewDir.z, 0, -viewDir.x);
  if (side.lengthSq() > .01) side.normalize().multiplyScalar(o.side || 0);
  const camPos = focus.clone()
    .addScaledVector(viewDir, o.distance ?? 58)
    .add(side);
  camPos.y = Math.max(camPos.y, focus.y + (o.height ?? 22));

  cinematic = {
    t: 0,
    life: o.life ?? 1.15,
    timeScale: o.timeScale ?? CINEMATIC_TIME_SCALE,
    priority,
    returnPoseName: o.returnPoseName || null,
  };
  camAnim = {
    pos: camPos,
    look: focus.clone().add(new THREE.Vector3(0, o.lookY ?? 2.5, 0)),
    rate: o.rate ?? 4.8,
  };
}

function updateCinematicClock(dt){
  if (!cinematic || simming) return 1;
  cinematic.t += dt;
  if (cinematic.t >= cinematic.life){
    const restore = cinematic.returnPoseName || (monster ? 'tactical' : 'hero');
    cinematic = null;
    flyToPose(restore);
    return 1;
  }
  return cinematic.timeScale;
}

/* ============================================================ */
/* 9. AUDIO (tiny WebAudio synth, no assets)                    */
/* ============================================================ */
const sfx = (() => {
  let ctx = null, master = null, alarmOsc = null, alarmGain = null, muted = false;
  const ensure = () => {
    if (!ctx){
      try {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        master = ctx.createGain(); master.gain.value = .28; master.connect(ctx.destination);
      } catch(e){ /* audio unsupported */ }
    }
    if (ctx && ctx.state === 'suspended') ctx.resume();
    return !!ctx;
  };
  const blip = (f0, f1, dur, type, g) => {
    if (muted || simming || !ensure()) return;
    const o = ctx.createOscillator(), gn = ctx.createGain();
    o.type = type; o.frequency.setValueAtTime(f0, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(Math.max(f1, 1), ctx.currentTime + dur);
    gn.gain.setValueAtTime(g, ctx.currentTime);
    gn.gain.exponentialRampToValueAtTime(.0001, ctx.currentTime + dur);
    o.connect(gn); gn.connect(master);
    o.start(); o.stop(ctx.currentTime + dur + .02);
  };
  const burst = (dur = .34, g = .22) => {
    if (muted || simming || !ensure()) return;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i=0;i<len;i++){
      const k = 1 - i/len;
      data[i] = (Math.random()*2 - 1) * k * k;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 850;
    const gn = ctx.createGain();
    gn.gain.setValueAtTime(g, ctx.currentTime);
    gn.gain.exponentialRampToValueAtTime(.0001, ctx.currentTime + dur);
    src.connect(lp); lp.connect(gn); gn.connect(master);
    src.start(); src.stop(ctx.currentTime + dur + .03);
  };
  return {
    unlock(){ ensure(); },
    get muted(){ return muted; },
    toggle(){ muted = !muted; if (muted) this.alarmOff(); return muted; },
    laser(blue){ blip(blue ? 760 : 960, blue ? 320 : 440, .07, 'square', .05); },
    zap(){ blip(240, 90, .2, 'sawtooth', .06); },
    thud(){ blip(110, 38, .3, 'sine', .35); blip(70, 30, .25, 'triangle', .25); },
    explode(){ blip(92, 26, .4, 'triangle', .56); blip(180, 54, .2, 'sawtooth', .18); burst(.42, .28); },
    roar(){ blip(150, 48, .65, 'sawtooth', .2); },
    chime(){ if (muted || simming || !ensure()) return;
      [620, 830, 1040].forEach((f, i) => setTimeout(() => blip(f, f, .22, 'sine', .12), i*110)); },
    alarmOn(){
      if (muted || simming || !ensure() || alarmOsc) return;
      alarmOsc = ctx.createOscillator(); alarmGain = ctx.createGain();
      alarmOsc.type = 'sawtooth'; alarmOsc.frequency.value = 520;
      alarmGain.gain.value = .035;
      const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 1200;
      alarmOsc.connect(lp); lp.connect(alarmGain); alarmGain.connect(master);
      alarmOsc.start();
    },
    alarmTick(phase){ if (alarmOsc) alarmOsc.frequency.setTargetAtTime(phase ? 524 : 388, ctx.currentTime, .02); },
    alarmOff(){
      if (alarmOsc){ try { alarmOsc.stop(); } catch(e){} alarmOsc = null; alarmGain = null; }
    },
  };
})();

/* ============================================================ */
/* 10. MONSTER                                                  */
/* ============================================================ */
const hpCanvas = document.createElement('canvas');
hpCanvas.width = 256; hpCanvas.height = 40;
const hpTexture = new THREE.CanvasTexture(hpCanvas);
hpTexture.colorSpace = THREE.SRGBColorSpace;
function drawHpBar(frac){
  const x = hpCanvas.getContext('2d');
  x.clearRect(0, 0, 256, 40);
  x.fillStyle = '#ffffff'; x.strokeStyle = '#d8dcea'; x.lineWidth = 4;
  x.beginPath(); x.roundRect(4, 6, 248, 28, 14); x.fill(); x.stroke();
  x.fillStyle = '#f45c54';
  if (frac > 0){ x.beginPath(); x.roundRect(9, 11, Math.max(18, 238*frac), 18, 9); x.fill(); }
  hpTexture.needsUpdate = true;
}

let monster = null;
function buildMonsterMesh(){
  const g = new THREE.Group();
  const bodyMat = std({ color: PAL.monsterBody, emissive: 0x000000 });
  const limbMat = std({ color: PAL.monsterLimb, emissive: 0x000000 });
  const spikeMat = std({ color: PAL.monsterSpike, emissive: 0x000000 });
  const box = (w,h,d,mat,x=0,y=0,z=0) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w,h,d), mat);
    m.position.set(x,y,z); m.castShadow = true; m.receiveShadow = true;
    return m;
  };

  const hips = { L: new THREE.Group(), R: new THREE.Group() };
  for (const [k, zz] of [['L', .8], ['R', -.8]]){
    hips[k].position.set(-.1, 1.7, zz);
    const leg = box(1.0, 1.7, 1.05, limbMat, 0, -.85, 0);
    hips[k].add(leg); g.add(hips[k]);
  }
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(1.7, 2.0, 6, 12), bodyMat);
  body.position.y = 3.4; body.scale.set(1.05, 1, .92); body.castShadow = true;
  g.add(body);
  const belly = new THREE.Mesh(new THREE.CapsuleGeometry(1.25, 1.5, 6, 12), std({ color: PAL.monsterBelly }));
  belly.position.set(.62, 3.2, 0); belly.scale.set(.8, .9, .8);
  g.add(belly);

  const shoulders = { L: new THREE.Group(), R: new THREE.Group() };
  for (const [k, zz] of [['L', 1.85], ['R', -1.85]]){
    shoulders[k].position.set(.2, 4.5, zz);
    shoulders[k].add(box(.6, 1.7, .65, limbMat, 0, -.8, 0));
    g.add(shoulders[k]);
  }

  const head = new THREE.Group();
  head.position.set(.45, 5.75, 0);
  head.add(box(1.75, 1.5, 1.6, bodyMat, 0, 0, 0));
  head.add(box(.95, .62, .9, limbMat, 1.05, -.35, 0));
  for (const zz of [.48, -.48]){
    const eye = new THREE.Mesh(new THREE.SphereGeometry(.17, 8, 8),
      std({ color: 0xff8ade, emissive: 0xff4fc8, emissiveIntensity: 2.2 }));
    eye.position.set(.86, .18, zz); head.add(eye);
    const horn = new THREE.Mesh(new THREE.ConeGeometry(.22, .75, 6), spikeMat);
    horn.position.set(-.3, .95, zz*1.05); horn.rotation.z = -.3; horn.rotation.x = zz > 0 ? .35 : -.35;
    horn.castShadow = true; head.add(horn);
  }
  g.add(head);

  for (let i=0;i<4;i++){
    const sp = new THREE.Mesh(new THREE.ConeGeometry(.38-(i*.06), 1.0-(i*.12), 5), spikeMat);
    sp.position.set(-.5-(i*.75), 4.7-(i*.55), 0);
    sp.rotation.z = .5; sp.castShadow = true; g.add(sp);
  }
  const tail = new THREE.Group(); tail.position.set(-1.7, 2.6, 0);
  for (let i=0;i<3;i++){
    const seg = new THREE.Mesh(new THREE.ConeGeometry(.62-(i*.17), 1.5, 6), limbMat);
    seg.rotation.z = Math.PI/2 + .35;
    seg.position.set(-(i*1.15)-.5, -(i*.25), 0);
    seg.castShadow = true; tail.add(seg);
  }
  g.add(tail);

  const bar = new THREE.Sprite(new THREE.SpriteMaterial({ map: hpTexture, transparent: true, depthTest: false }));
  bar.position.y = 8.4; bar.scale.set(4.4, .69, 1);
  bar.renderOrder = 999;
  g.add(bar);

  // threat marker - visible through buildings so the monster is never lost
  const marker = new THREE.Mesh(new THREE.RingGeometry(1.9, 2.5, 36),
    new THREE.MeshBasicMaterial({ color: 0xff7a72, transparent: true, opacity: .4, depthTest: false, side: THREE.DoubleSide }));
  marker.rotation.x = -Math.PI/2; marker.position.y = .15;
  marker.renderOrder = 998;
  g.add(marker);

  g.scale.setScalar(MON_SCALE);
  g.traverse(o => { if (o.isMesh){ o.castShadow = true; } });
  return { g, hips, shoulders, head, tail, body, bar, marker, mats: [bodyMat, limbMat, spikeMat] };
}

function spawnMonster(point, forceMode){
  if (monster) return false;
  ensureBattleTraceForSpawn();
  const edge = Math.max(Math.abs(point.x), Math.abs(point.z)) > 115;
  const mode = forceMode || (edge ? 'edge' : 'drop');
  const parts = buildMonsterMesh();
  monster = {
    ...parts,
    hp: MONSTER_HP, maxHp: MONSTER_HP,
    pos: new THREE.Vector3(), heading: 0, wp: 1, path: [],
    state: mode === 'drop' ? 'dropping' : 'entering',
    t: 0, walkT: 0, flash: 0, poundT: 0, landed: false,
    baseSpeed: MONSTER_BASE_SPEED, speed: MONSTER_BASE_SPEED,
    berserk: false, attackT: 0, attackAnim: 0,
    knockT: 0, knockDir: new THREE.Vector3(),
  };
  drawHpBar(1);

  if (mode === 'drop'){
    const ground = snapToRoad(point);
    monster.pos.copy(ground);
    monster.dropY = 110;
    monster.g.position.set(ground.x, 110, ground.z);
    monster.g.scale.set(.95*MON_SCALE, 1.5*MON_SCALE, .95*MON_SCALE);
  } else {
    // walk in from the nearest platform edge along a road line
    const ax = Math.abs(point.x) > Math.abs(point.z) ? 'x' : 'z';
    const s = Math.sign(ax === 'x' ? point.x : point.z) || 1;
    let lane = L[0], bd = 1e9;
    for (const c of L){
      const d = Math.abs((ax === 'x' ? point.z : point.x) - c);
      if (d < bd){ bd = d; lane = c; }
    }
    const ext = ROAD_EXT(lane) - 2; // wade ashore at the end of the road
    const entry = ax === 'x' ? new THREE.Vector3(ext*s, 0, lane) : new THREE.Vector3(lane, 0, ext*s);
    monster.pos.copy(entry);
    monster.g.position.copy(entry);
    monster.g.scale.setScalar(.02);
    puffFx(entry.clone().setY(1.5), 8);
  }
  scene.add(monster.g);
  recordBattleEvent('spawn', {
    mode,
    requestedPoint: traceVec(point),
    position: traceVec(monster.pos),
    viewport: traceViewport(),
    camera: cameraSnapshot(),
  });
  setHint('A monster has appeared');
  followMonsterCamera = true;
  flyToPose('tactical');
  return true;
}

function monsterRoute(){
  const path = routeTo(monster.pos, PLAZA_CORNERS);
  const last = path[path.length-1];
  const approach = last.clone().normalize().multiplyScalar(HQ_STOP_R);
  path.push(approach);
  monster.path = path; monster.wp = 1;
  recordBattleEvent('route', { path: path.map(traceVec) });
}

function monsterSpeed(m){
  return m.baseSpeed * (m.berserk ? MONSTER_BERSERK_MULT : 1);
}

function monsterKnockOffset(m){
  if (m.knockT <= 0) return new THREE.Vector3();
  const k = Math.sin((m.knockT / MONSTER_KNOCK_TIME) * Math.PI);
  return m.knockDir.clone().multiplyScalar(MONSTER_KNOCK_DIST * k);
}

function placeMonster(m, y = groundY(m.pos.x, m.pos.z)){
  const off = monsterKnockOffset(m);
  m.g.position.set(m.pos.x + off.x, y, m.pos.z + off.z);
}

function triggerBerserk(m){
  if (m.berserk || m.hp <= 0) return;
  m.berserk = true;
  m.speed = monsterSpeed(m);
  m.walkT += Math.PI * .25;
  m.marker.material.color.set(0xff5c54);
  m.marker.material.opacity = .62;
  ringFx(m.pos, 0xff7a72, 18, .55);
  setHint('Monster berserk - rushing the HQ');
  addShake(.25); sfx.roar();
}

function horizontalDist(a, b){
  return Math.hypot(a.x - b.x, a.z - b.z);
}

function disableTrafficCar(c, pos){
  c.disabledT = 4.5;
  c.g.visible = false;
  puffFx(pos.clone().setY(.8), 8, 0xd8dce6, 9);
  sparkFx(pos.clone().setY(1.2), 0xffb3ad);
}

function hidePairFromAttack(p, pos){
  p.state = 'hidden';
  p.a.visible = p.b.visible = false;
  p.a.userData.bub.scale.set(0,0,1);
  p.b.userData.bub.scale.set(0,0,1);
  puffFx(pos.clone().setY(1), 6, 0xe7dfd2, 7);
}

function monsterAttackNearby(m, dt){
  if (!m.landed || m.state === 'dying') return;
  m.attackT = Math.max(0, m.attackT - dt);
  if (m.attackT > 0) return;

  const reach = MONSTER_ATTACK_RANGE + (m.berserk ? 4 : 0);
  let best = null;
  const consider = (type, obj, pos, priority) => {
    const d = horizontalDist(m.pos, pos);
    if (d > reach) return;
    if (!best || priority < best.priority || (priority === best.priority && d < best.d))
      best = { type, obj, pos: pos.clone(), priority, d };
  };

  for (const d of drones){
    if (d.state === 'docked' || d.state === 'launching' || d.state === 'destroyed' || d.state === 'falling') continue;
    if (d.pos.y > 23) continue;
    consider('drone', d, d.pos, 0);
  }
  for (const c of traffic){
    if ((c.disabledT || 0) > 0 || !c.g.visible) continue;
    consider('car', c, c.g.position, 1);
  }
  for (const p of pairs){
    if (p.state === 'hidden') continue;
    if (p.a.visible) consider('pair', p, p.a.position, 2);
    if (p.b.visible) consider('pair', p, p.b.position, 2);
  }
  if (!best) return;

  m.attackT = m.berserk ? .34 : .62;
  m.attackAnim = MONSTER_ATTACK_ANIM;
  m.g.rotation.y = lerpAngle(m.g.rotation.y,
    yawFromDir(best.pos.x - m.pos.x, best.pos.z - m.pos.z), .8);
  recordBattleEvent('monsterAttack', {
    targetType: best.type,
    target: best.type === 'drone' ? `drone-${drones.indexOf(best.obj) + 1}` : best.type,
    distance: +best.d.toFixed(2),
    monster: traceVec(m.pos),
    targetPos: traceVec(best.pos),
  });
  if (best.type === 'drone') damageDrone(best.obj);
  else if (best.type === 'car') disableTrafficCar(best.obj, best.pos);
  else hidePairFromAttack(best.obj, best.pos);
  ringFx(m.pos.clone().lerp(best.pos, .45), 0xff8e86, 5.5, .28, 1.2);
  addShake(.18); sfx.thud();
}

function poseMonsterAttack(m){
  if (m.attackAnim <= 0) return;
  const k = Math.sin((m.attackAnim / MONSTER_ATTACK_ANIM) * Math.PI);
  m.shoulders.L.rotation.z -= 1.25 * k;
  m.shoulders.R.rotation.z += .75 * k;
  m.head.rotation.x -= .16 * k;
  m.body.rotation.z += .12 * k;
}

function damageMonster(d, hitPos, sourcePos, source = 'unknown'){
  if (!monster || monster.state === 'dying' || !monster.landed) return;
  const hpBefore = monster.hp;
  monster.hp -= d; monster.flash = .14;
  recordBattleDamage(source, d, hpBefore, monster.hp, hitPos, sourcePos);
  if (sourcePos){
    const dir = monster.pos.clone().sub(sourcePos); dir.y = 0;
    if (dir.lengthSq() < .01) dir.copy(monster.pos);
    if (dir.lengthSq() > .01){
      monster.knockDir.copy(dir.normalize());
      monster.knockT = MONSTER_KNOCK_TIME;
    }
  }
  drawHpBar(Math.max(0, monster.hp/monster.maxHp));
  if (hitPos) sparkFx(hitPos);
  if (monster.hp <= 0) killMonster();
  else if (monster.hp / monster.maxHp <= MONSTER_BERSERK_AT) triggerBerserk(monster);
}

function killMonster(){
  recordBattleEvent('kill', { position: traceVec(monster ? monster.pos : null), hp: monster ? +monster.hp.toFixed(2) : null });
  followMonsterCamera = false;
  monster.state = 'dying'; monster.t = 0;
  monster.bar.visible = false;
  game.kills++;
  game.reequipPending = drones.some(d => d.state === 'destroyed' || d.state === 'falling');
  const deathFocus = monster.g.position.clone().add(new THREE.Vector3(0, 6, 0));
  startCinematicFocus(deathFocus, {
    priority: 2, life: 1.65, timeScale: .24, distance: 82, height: 36,
    lookY: 1.5, side: 10, returnPoseName: 'tactical',
  });
  explosionFx(deathFocus, 1.65, 0xff7a72);
  setAlarm(false);
  setChip('neutral');
  setHint('Threat neutralized - agents returning to HQ');
  sfx.chime();
  for (const a of allAgents()){
    if (a.kind === 'drone' && (a.state === 'destroyed' || a.state === 'falling')) continue;
    if (a.state !== 'docked') a.state = 'returnPrep', a.timer = .4 + Math.random()*.8;
  }
}

function updateMonster(dt){
  if (!monster) return;
  const m = monster;
  m.t += dt;
  if (m.knockT > 0) m.knockT = Math.max(0, m.knockT - dt);
  if (m.attackAnim > 0) m.attackAnim = Math.max(0, m.attackAnim - dt);

  // damage flash
  if (m.flash > 0){
    m.flash -= dt;
    const k = Math.max(0, m.flash/.14);
    for (const mat of m.mats){ mat.emissive.setRGB(k, k, k); mat.emissiveIntensity = .8; }
  } else if (m.berserk){
    for (const mat of m.mats){
      mat.emissive.set(0xff4f47);
      mat.emissiveIntensity = .14 + Math.sin(game.t*10)*.05;
    }
  } else {
    for (const mat of m.mats){ mat.emissive.setHex(0x000000); mat.emissiveIntensity = 0; }
  }

  if (m.state === 'dropping'){
    m.dropY -= dt * (48 + (110 - m.dropY) * 2.6); // accelerating fall
    if (m.dropY <= 0){
      m.dropY = 0; m.state = 'impact'; m.t = 0; m.landed = true;
      m.g.scale.set(1.3*MON_SCALE, .8*MON_SCALE, 1.3*MON_SCALE);
      puffFx(m.pos.clone().setY(1), 14, 0xe7dfd2, 14);
      ringFx(m.pos, 0xd9cfc0, 16, .55);
      addShake(.55); sfx.thud();
    }
    m.g.position.y = m.dropY;
    return;
  }
  if (m.state === 'impact'){
    const k = Math.min(1, m.t/.35);
    m.g.scale.set(1.3 - .3*k, .8 + .2*k, 1.3 - .3*k);
    m.g.scale.multiplyScalar(MON_SCALE);
    if (k >= 1){
      m.g.scale.setScalar(MON_SCALE);
      m.state = 'rising'; m.t = 0;
      sfx.roar();
      triggerAlarm();
      monsterRoute();
    }
    return;
  }
  if (m.state === 'entering'){
    const k = Math.min(1, m.t/.55);
    m.g.scale.setScalar(.02 + (MON_SCALE-.02)*k);
    m.landed = k > .6;
    if (k >= 1){
      m.state = 'rising'; m.t = 0;
      sfx.roar(); triggerAlarm(); monsterRoute();
    }
    return;
  }
  if (m.state === 'rising'){ // brief roar beat before marching
    placeMonster(m);
    if (m.t > .5){ m.state = 'walking'; }
    m.head.rotation.x = -Math.sin(Math.min(1, m.t*2)*Math.PI)*.3;
    return;
  }

  if (m.state === 'walking'){
    m.speed = monsterSpeed(m);
    m.walkT += dt*(m.berserk ? 8.5 : 5);
    const done = walkPath(m, m.speed, dt);
    placeMonster(m);
    m.g.rotation.y = lerpAngle(m.g.rotation.y, m.heading, dt*6);
    monsterAttackNearby(m, dt);
    // stomp cycle
    m.hips.L.rotation.z = Math.sin(m.walkT)*.5;
    m.hips.R.rotation.z = -Math.sin(m.walkT)*.5;
    m.shoulders.L.rotation.z = -Math.sin(m.walkT)*.35;
    m.shoulders.R.rotation.z = Math.sin(m.walkT)*.35;
    m.body.position.y = 3.4 + Math.abs(Math.sin(m.walkT))*.22;
    m.g.rotation.z = Math.sin(m.walkT)*.045;
    m.tail.rotation.y = Math.sin(m.walkT*.6)*.3;
    poseMonsterAttack(m);
    if (done){ m.state = 'pounding'; m.poundT = 0; }
    return;
  }

  if (m.state === 'pounding'){ // hammering the HQ shield
    m.poundT += dt;
    placeMonster(m);
    monsterAttackNearby(m, dt);
    const cycle = 1.6, k = (m.poundT % cycle)/cycle;
    const target = yawFromDir(-m.pos.x, -m.pos.z);
    m.g.rotation.y = lerpAngle(m.g.rotation.y, target, dt*5);
    if (k < .55){ // windup
      const w = k/.55;
      m.shoulders.L.rotation.z = m.shoulders.R.rotation.z = -2.2*w;
      m.body.rotation.z = .18*w;
    } else { // slam
      const s = (k-.55)/.45;
      m.shoulders.L.rotation.z = m.shoulders.R.rotation.z = -2.2 + 2.8*Math.min(1, s*2.5);
      m.body.rotation.z = .18 - .35*Math.min(1, s*2.5);
      if (!m.slammed && s > .35){
        m.slammed = true;
        shieldPulse = 1;
        ringFx(m.pos.clone().lerp(new THREE.Vector3(0,0,0), .25), 0x8df2d6, 8, .45, 4.5);
        addShake(.3); sfx.thud();
      }
    }
    if (k < .1) m.slammed = false;
    poseMonsterAttack(m);
    return;
  }

  if (m.state === 'dying'){
    m.marker.visible = false;
    const k = Math.min(1, m.t/1.3);
    if (k < .5){
      m.g.rotation.z = (k/.5)*1.45;
      m.g.position.y = groundY(m.pos.x, m.pos.z) + Math.sin((k/.5)*Math.PI)*1.4;
    } else {
      if (!m.burst){
        m.burst = true;
        explosionFx(m.pos.clone().setY(5.5), 1.25, 0xfff3a0);
        confettiFx(m.pos.clone().setY(6));
        ringFx(m.pos, 0x7df5d4, 20, .7);
        addShake(.4);
      }
      const s = 1 - (k-.5)/.5;
      m.g.scale.setScalar(Math.max(.01, MON_SCALE*s));
    }
    if (k >= 1){
      scene.remove(m.g);
      recordBattleEvent('removed', { position: traceVec(m.pos) });
      monster = null;
      if (game.reequipPending) setHint('Drones returning to HQ for re-equipping');
      else finishBattleReady();
    }
  }
}

/* ============================================================ */
/* 11. AGENTS - drones, police cars, mechs                      */
/* ============================================================ */
function makeDrone(){
  // quad camera drone: chamfered two-tier shell, mint seam line, motor pods
  // with banded caps, two-blade props, splayed landing legs, front gimbal
  const g = new THREE.Group();
  const add = (geo, m, x, y, z, cs = true) => { const b = new THREE.Mesh(geo, m);
    b.position.set(x, y, z); b.castShadow = cs; b.receiveShadow = true; g.add(b); return b; };
  add(new RoundedBoxGeometry(1.04, .2, .76, 2, .08), WHITE, 0, 0, 0);    // lower hull
  add(new THREE.BoxGeometry(.92, .04, .66), GLOW, .01, .105, 0, false);  // mint seam
  add(new RoundedBoxGeometry(.72, .18, .52, 2, .07), WHITE, .02, .19, 0);// canopy
  const rotors = [];
  for (const [sx, sz] of [[1,1],[-1,1],[1,-1],[-1,-1]]){
    const arm = add(new THREE.BoxGeometry(.5, .055, .13), WHITE, sx*.48, .02, sz*.48);
    arm.rotation.y = -sx*sz*Math.PI/4;
    add(new THREE.CylinderGeometry(.1, .125, .18, 10), WHITE, sx*.68, .04, sz*.68);
    add(new THREE.CylinderGeometry(.115, .115, .05, 10), GLOW, sx*.68, .145, sz*.68, false);
    add(new THREE.CylinderGeometry(.08, .095, .11, 10), WHITE, sx*.68, .22, sz*.68);
    add(new THREE.CylinderGeometry(.045, .055, .05, 10), GLOW, sx*.68, .295, sz*.68, false);
    const blades = new THREE.Group();
    blades.position.set(sx*.68, .34, sz*.68);
    blades.rotation.y = (sx + sz*.5)*.7;
    const bar = new THREE.Mesh(new THREE.BoxGeometry(.8, .024, .085), WHITE);
    bar.castShadow = false; blades.add(bar);
    g.add(blades); rotors.push(blades);
    const leg = add(new THREE.BoxGeometry(.05, .26, .05), WHITE, sx*.44, -.15, sz*.3);
    leg.rotation.z = -sx*.3; leg.rotation.x = sz*.24;
  }
  add(new THREE.BoxGeometry(.07, .08, .07), DARKM, .3, -.13, 0, false);  // gimbal mount
  add(new RoundedBoxGeometry(.24, .2, .2, 2, .05), WHITE, .33, -.22, 0); // camera housing
  const lens = add(new THREE.CylinderGeometry(.05, .05, .05, 10), GLOW, .455, -.22, 0, false);
  lens.rotation.z = Math.PI/2;
  g.scale.setScalar(1.6); // fits the V2 city scale
  return { g, rotors };
}

// white low-poly cruiser (models/police-car.html) - front toward +x
function makePoliceCar(){
  const g = new THREE.Group();
  const box = (w, h, d, m, x = 0, y = 0, z = 0, s = true) => {
    const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
    b.position.set(x, y, z); b.castShadow = s; b.receiveShadow = true;
    g.add(b); return b;
  };
  const cyl = (rt, rb, h, seg, m, x, y, z) => {
    const c = new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), m);
    c.position.set(x, y, z); c.castShadow = true; c.receiveShadow = true;
    g.add(c); return c;
  };
  const BLUE = std({ color: 0x4f86dd, roughness: .8 });
  const CHECK = std({ color: 0x7df0d2, emissive: MINT, emissiveIntensity: .22 });
  const HANDLE = std({ color: 0xc3c9d4 });

  box(2.62, .2, 1.22, WHITE, 0, .4, 0);             // chassis / bumper band
  box(2.6, .34, 1.24, WHITE, 0, .65, 0);            // main shell, hood + trunk
  box(1.22, .32, 1.06, GLASSM, -.185, .98, 0);      // cabin side glass
  box(.07, .36, 1.1, WHITE, -.18, .98, 0, false);   // B-pillar
  box(1.2, .1, 1.12, WHITE, -.185, 1.19, 0);        // roof
  const wind = box(.47, .24, 1.04, GLASSM, .547, .925, 0);
  wind.rotation.z = -.69;
  const rear = box(.42, .24, 1.04, GLASSM, -.877, .937, 0);
  rear.rotation.z = .79;

  const wheel = (x, z) => {
    const t = cyl(.27, .27, .18, 8, TIRE, x, .27, z); t.rotation.x = Math.PI/2;
    const h = cyl(.13, .13, .2, 8, HUB, x, .27, z);   h.rotation.x = Math.PI/2;
  };
  wheel(.85, .57); wheel(.85, -.57); wheel(-.85, .57); wheel(-.85, -.57);

  for (const sz of [1, -1]){
    const z = .625 * sz;
    box(2.34, .1, .025, BLUE, -.08, .77, z, false);
    for (let i = 0; i < 7; i++){
      box(.12, .12, .025, CHECK, -1.02 + i*.24, .67, z, false);
      box(.12, .12, .025, CHECK, -.9 + i*.24, .55, z, false);
    }
    box(.2, .05, .035, HANDLE, .07, .77, z, false);
    box(.1, .04, .04, WHITE, .47, .92, .61*sz, false);
    box(.07, .12, .06, WHITE, .5, .92, .68*sz, false);
  }

  box(.05, .14, .34, HEAD, 1.31, .7, .4, false);
  box(.05, .14, .34, HEAD, 1.31, .7, -.4, false);
  box(.05, .08, .26, GLOW, -1.31, .68, .42, false);
  box(.05, .08, .26, GLOW, -1.31, .68, -.42, false);

  for (const pz of [.3, -.3]){
    box(.06, .4, .08, WHITE, 1.44, .5, pz);
    box(.12, .06, .08, WHITE, 1.36, .62, pz);
  }
  box(.06, .08, .8, WHITE, 1.44, .66, 0, false);
  box(.06, .08, .8, WHITE, 1.44, .4, 0, false);
  box(.05, .26, .06, WHITE, 1.44, .53, .1, false);
  box(.05, .26, .06, WHITE, 1.44, .53, -.1, false);

  // roof light bar: mint / blue / mint, flashed by the global alarm
  box(.2, .05, .8, DARKM, -.12, 1.265, 0, false);
  box(.16, .12, .22, copMintMat, -.12, 1.35, .27, false);
  box(.16, .12, .34, copBlueMat, -.12, 1.35, 0, false);
  box(.16, .12, .22, copMintMat, -.12, 1.35, -.27, false);
  return { g };
}

// white armored mech (models/mech-robot.html) - front toward +x, legs in swing groups
const MECHMAT = {
  INNER: std({ color: 0x7d8594 }),
  JOINT: std({ color: 0x59616e }),
  MUZ:   std({ color: 0x3a4049 }),
  BLUE:  std({ color: 0x4f86dd, roughness: .8 }),
  CYAN:  std({ color: 0x9ffcf0, emissive: 0x35e8d8, emissiveIntensity: 1.3 }),
  EMBLM: std({ color: 0x9ff5dd, emissive: MINT, emissiveIntensity: .4 }),
  VENTG: std({ color: 0x9ff5dd, emissive: MINT, emissiveIntensity: .9 }),
  BARB:  std({ color: 0xa9c6ff, emissive: 0x2f6fe8, emissiveIntensity: 1.1 }),
  BARM:  std({ color: 0x9ff5dd, emissive: MINT, emissiveIntensity: 1.1 }),
  BARBC: std({ color: 0xcfe0ff, emissive: 0x5b9cff, emissiveIntensity: 1.4 }),
  BARMC: std({ color: 0xdcfff5, emissive: MINT, emissiveIntensity: 1.6 }),
};
tick((dt, t) => { // light bars sweep opposite phases, visor breathes
  MECHMAT.BARB.emissiveIntensity = 1.1 + Math.sin(t*4) * .55;
  MECHMAT.BARM.emissiveIntensity = 1.1 - Math.sin(t*4) * .55;
  MECHMAT.CYAN.emissiveIntensity = 1.3 + Math.sin(t*2) * .15;
});

function makeRobot(){
  const M = MECHMAT;
  const g = new THREE.Group();
  const radd = (parent, w, h, d, m, x, y, z, s = true, r = .04) => {
    const rr = Math.min(r, w * .45, h * .45, d * .45);
    const b = new THREE.Mesh(new RoundedBoxGeometry(w, h, d, 2, rr), m);
    b.position.set(x, y, z); b.castShadow = s; b.receiveShadow = true;
    parent.add(b); return b;
  };
  const cadd = (parent, rt, rb, h, seg, m, x, y, z) => {
    const c = new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), m);
    c.position.set(x, y, z); c.castShadow = true; c.receiveShadow = true;
    parent.add(c); return c;
  };
  const box = (...a) => radd(g, ...a);
  const cyl = (...a) => cadd(g, ...a);

  // legs in hip groups so the march cycle can swing them
  const hips = { L: new THREE.Group(), R: new THREE.Group() };
  for (const [k, zz] of [['L', .42], ['R', -.42]]){
    const hip = hips[k];
    hip.position.set(0, 2.06, zz);
    g.add(hip);
    radd(hip, .74, .12, .62, GLOW, .06, -2.0, 0, false);     // glowing sole
    radd(hip, .7, .3, .58, WHITE, .05, -1.81, 0, true, .08); // boot
    radd(hip, .24, .18, .46, WHITE, .44, -1.84, 0, true, .07); // toe cap
    radd(hip, .3, .22, .3, M.JOINT, 0, -1.6, 0);             // ankle
    const sh = cadd(hip, .26, .32, .75, 4, WHITE, 0, -1.11, 0); // calf
    sh.rotation.y = Math.PI/4;
    radd(hip, .52, .32, .54, WHITE, 0, -.58, 0, true, .09);  // knee block
    const pad = cadd(hip, .15, .15, .06, 6, M.BLUE, .28, -.56, 0);
    pad.rotation.z = Math.PI/2;
    const th = cadd(hip, .31, .24, .48, 4, WHITE, 0, -.22, 0); // thigh
    th.rotation.y = Math.PI/4;
    radd(hip, .3, .2, .32, M.JOINT, 0, 0, 0);                // hip joint
  }
  // pelvis + abdomen stack
  box(.85, .34, .78, M.INNER, 0, 2.18, 0, true, .08);
  box(.16, .26, .5, WHITE, .45, 2.16, 0);
  box(.78, .34, .68, WHITE, 0, 2.5, 0, true, .08);
  box(.04, .07, .26, M.EMBLM, .4, 2.5, 0, false);
  box(.62, .25, .6, M.JOINT, 0, 2.72, 0);
  // torso - faceted chest tapering to the waist
  const chest = cyl(.66, .52, .7, 8, WHITE, 0, 3.13, 0);
  chest.geometry.rotateY(Math.PI/8);
  chest.scale.x = .8;
  box(.95, .28, 1.0, WHITE, 0, 3.55, 0, true, .09);
  box(.06, .22, .28, M.EMBLM, .46, 3.2, 0, false);
  const emb = box(.05, .2, .2, M.EMBLM, .46, 3.07, 0, false);
  emb.rotation.x = Math.PI/4;
  box(.04, .07, .1, M.EMBLM, .45, 3.2, -.2, false);
  // shoulder pods with light bars (blue = cannon side, mint = shield side)
  for (const zz of [.95, -.95]){
    box(.5, .3, .4, M.JOINT, 0, 3.4, zz * .62);
    box(.7, .55, .68, WHITE, 0, 3.45, zz, true, .12);
    box(.45, .22, .06, M.BLUE, .05, 3.42, zz + Math.sign(zz)*.36, false);
    box(.06, .2, .3, M.BLUE, .36, 3.45, zz, false);
    box(.05, .04, .42, M.VENTG, .31, 3.2, zz, false);
    box(.2, .14, .34, M.JOINT, 0, 3.8, zz);
    box(.55, .18, .34, zz < 0 ? M.BARB : M.BARM, 0, 3.96, zz, true, .07);
    box(.42, .07, .26, zz < 0 ? M.BARBC : M.BARMC, 0, 4.04, zz, false);
    box(.28, .45, .28, M.JOINT, .05, 2.95, zz);
    box(.32, .28, .32, M.JOINT, .1, 2.65, zz);
  }
  // right arm cannon, tilted down from the elbow
  const gun = new THREE.Group();
  gun.position.set(.1, 2.65, -.95);
  gun.rotation.z = -.32;
  g.add(gun);
  radd(gun, 1.1, .5, .5, WHITE, .4, -.23, 0, true, .09);
  radd(gun, .45, .1, .22, M.INNER, .05, .07, 0);
  for (const gz of [.26, -.26]){
    radd(gun, .4, .2, .05, M.BLUE, .25, -.2, gz);
    radd(gun, .05, .24, .05, M.VENTG, .72, -.31, gz);
    radd(gun, .05, .24, .05, M.VENTG, .84, -.31, gz);
  }
  const muzzleMesh = cadd(gun, .17, .17, .26, 8, M.JOINT, 1.06, -.23, 0);
  muzzleMesh.rotation.z = Math.PI/2;
  const bore = cadd(gun, .09, .09, .28, 8, M.MUZ, 1.08, -.23, 0);
  bore.rotation.z = Math.PI/2;
  // left arm + riot shield
  box(.5, .3, .4, WHITE, .15, 2.5, .95);
  box(.2, .2, .4, M.JOINT, .2, 2.35, 1.1);
  box(.16, 2.05, .98, WHITE, .3, 2.0, 1.25, true, .07);
  box(.1, .48, .72, M.INNER, .39, 2.62, 1.25, false);
  box(.07, .26, .58, M.CYAN, .44, 2.62, 1.25, false);
  box(.07, 1.05, .68, M.BLUE, .4, 1.62, 1.25, false);
  // head
  cyl(.12, .15, .22, 8, M.JOINT, 0, 3.66, 0);
  box(.55, .12, .6, M.INNER, .02, 3.6, 0);
  box(.6, .52, .64, WHITE, 0, 4.0, 0, true, .16);
  box(.05, .3, .5, M.INNER, .29, 3.92, 0, false);
  box(.07, .15, .46, M.CYAN, .32, 3.97, 0, false);
  box(.32, .16, .42, M.INNER, .14, 3.76, 0);
  box(.05, .05, .3, WHITE, .31, 3.76, 0, false);
  box(.06, .09, .2, M.BLUE, .29, 4.14, 0, false);
  box(.2, .07, .3, M.BLUE, 0, 4.25, 0, false);
  box(.07, .12, .1, M.BLUE, .14, 4.0, .31, false);
  box(.07, .12, .1, M.BLUE, .14, 4.0, -.31, false);

  const muzzle = new THREE.Object3D(); muzzle.position.set(1.06, -.23, 0); gun.add(muzzle);
  const muzzleGlow = new THREE.Mesh(new THREE.SphereGeometry(.18, 12, 8), additiveMat(0xbfffe9, 0));
  muzzleGlow.position.copy(muzzle.position);
  muzzleGlow.visible = false; muzzleGlow.renderOrder = 1001;
  gun.add(muzzleGlow);
  g.scale.setScalar(2); // city defense mechs tower over civilian traffic
  return { g, hips, gun, muzzle, muzzleGlow };
}

const drones = [], cops = [], bots = [];
{
  for (let i=0;i<dronePads.length;i++){ // one drone per HQ roof pad (6)
    const d = makeDrone();
    const pad = dronePads[i].clone().add(new THREE.Vector3(0, .45, 0));
    d.g.position.copy(pad);
    scene.add(d.g);
    drones.push({ kind: 'drone', ...d, state: 'docked', pad, timer: 0, fireT: 0,
      hp: DRONE_MAX_HITS, orbitA: i*(Math.PI*2/dronePads.length),
      orbitR: 15.5 + (i % 3)*2.6, orbitH: 11.4 + (i % 2)*1.7,
      orbitSpeed: .86 + (i % 4)*.12, attackLane: i % 3, runSide: i % 2 ? 1 : -1,
      phase: i*.93, run: null, pos: pad.clone() });
  }
  for (let i=0;i<2;i++){
    const c = makePoliceCar();
    const bay = new THREE.Vector3(i === 0 ? -3.4 : 3.4, 0, 23.2); // south walkway, behind the wall opening
    c.g.position.set(bay.x, groundY(bay.x, bay.z), bay.z); c.g.visible = false;
    scene.add(c.g);
    cops.push({ kind: 'cop', ...c, state: 'docked', bay, timer: 0, fireT: 0,
      pos: bay.clone(), heading: -Math.PI/2, wp: 1, path: [], repathT: 0 });
  }
  for (let i=0;i<2;i++){
    const r = makeRobot();
    const bay = new THREE.Vector3(i === 0 ? -11.2 : 11.2, 0, 21.2); // split bays keep the large mechs from overlapping
    r.g.position.set(bay.x, groundY(bay.x, bay.z), bay.z); r.g.visible = false;
    scene.add(r.g);
    bots.push({ kind: 'bot', ...r, state: 'docked', bay, timer: 0, fireT: 0, shotT: 0, walkT: 0,
      pos: bay.clone(), heading: -Math.PI/2, slot: i === 0 ? -1 : 1, wp: 1, path: [], repathT: 0 });
  }
}
const allAgents = () => [...drones, ...cops, ...bots];

function dispatchAgents(){
  const delays = { drone: [.7, 1.0, 1.3, 1.6, 1.9, 2.2], cop: [2.4, 3.2], bot: [4.4, 5.4] };
  const idx = { drone: 0, cop: 0, bot: 0 };
  for (const a of allAgents()){
    if (a.kind === 'drone' && (a.state === 'destroyed' || a.state === 'falling')) continue;
    if (a.kind === 'drone') a.run = null;
    if (a.state === 'docked' || a.state === 'returnPrep' || a.state === 'returning'){
      a.timer = a.state === 'docked' ? delays[a.kind][idx[a.kind]++] : .3;
      a.state = 'launching';
    } else {
      a.state = 'engaging'; // already out - re-task immediately
    }
  }
  recordBattleEvent('dispatchAgents', { delays });
}

const monsterChest = () => monster ? monster.g.position.clone().add(new THREE.Vector3(0, 8.8, 0)) : null;
const monsterAliveTargetable = () => monster && monster.landed && monster.state !== 'dying';
function updateMonsterCameraFollow(rawDt){
  if (!followMonsterCamera || cinematic) return;
  const target = monsterChest();
  if (!target || !monster || monster.state === 'dying'){
    followMonsterCamera = false;
    return;
  }
  const k = 1 - Math.exp(-rawDt * (camAnim ? 3.2 : 5.4));
  monsterCameraFollowFocus.copy(orbit.target).lerp(target, k);
  monsterCameraFollowDelta.copy(monsterCameraFollowFocus).sub(orbit.target);
  orbit.target.copy(monsterCameraFollowFocus);
  camera.position.add(monsterCameraFollowDelta);
  activeCameraPoseName = null;
}

function keepOutsideHqAir(p, minR = 29){
  const len = Math.hypot(p.x, p.z);
  if (len < minR){
    const s = minR / Math.max(len, .001);
    p.x *= s; p.z *= s;
  }
  return p;
}

function droneOrbitPoint(a, center){
  const r = a.orbitR + Math.sin(game.t*1.15 + a.phase)*1.6;
  const h = a.orbitH + Math.sin(a.orbitA*2.1 + a.phase)*1.25;
  return keepOutsideHqAir(new THREE.Vector3(
    center.x + Math.cos(a.orbitA)*r,
    h,
    center.z + Math.sin(a.orbitA)*r,
  ));
}

function startDroneAttackRun(a, center){
  const source = `drone-${drones.indexOf(a) + 1}`;
  const side = a.runSide || 1;
  const lane = a.attackLane || 0;
  const approach = a.orbitA - side*(.72 + lane*.12);
  const pass = keepOutsideHqAir(new THREE.Vector3(
    center.x + Math.cos(approach)*(8.8 + lane*1.2),
    8.8 + lane*.8,
    center.z + Math.sin(approach)*(8.8 + lane*1.2),
  ), 28);
  const apex = a.pos.clone().lerp(pass, .48);
  apex.y = Math.max(apex.y, pass.y) + 3.6 + lane*.7;
  const exitA = approach + side*(1.45 + lane*.13);
  const exit = keepOutsideHqAir(new THREE.Vector3(
    center.x + Math.cos(exitA)*(a.orbitR + 5.5 + lane*1.2),
    a.orbitH + 1.2,
    center.z + Math.sin(exitA)*(a.orbitR + 5.5 + lane*1.2),
  ));
  a.run = {
    t: 0,
    dur: .82 + lane*.12,
    fired: false,
    side,
    curve: new THREE.CatmullRomCurve3([a.pos.clone(), apex, pass, exit], false, 'catmullrom', .5),
  };
  recordBattleEvent('droneRunStart', {
    source,
    lane,
    orbitA: +a.orbitA.toFixed(3),
    position: traceVec(a.pos),
    center: traceVec(center),
    targetDistance: monsterChest() ? +a.pos.distanceTo(monsterChest()).toFixed(2) : null,
  });
  a.runSide = -side;
}

function fireDroneVolley(a){
  const target = monsterChest();
  if (!target) return;
  const source = `drone-${drones.indexOf(a) + 1}`;
  const from = a.pos.clone().add(new THREE.Vector3(0, -.4, 0));
  const dir = target.clone().sub(from);
  dir.y = 0;
  if (dir.lengthSq() < .01) dir.set(Math.cos(a.orbitA), 0, Math.sin(a.orbitA));
  dir.normalize();
  const side = new THREE.Vector3(-dir.z, 0, dir.x).multiplyScalar(.48);
  flashFx(from, 0x7df5d4, .95);
  recordBattleEvent('droneVolley', { source, hp: monster ? +monster.hp.toFixed(2) : null, position: traceVec(a.pos) });
  fireLaserLine(from.clone().add(side), DRONE_LASER_DAMAGE, 0x7df5d4, { source });
  fireLaserLine(from.clone().addScaledVector(side, -1), DRONE_LASER_DAMAGE, 0x7df5d4, { source, sound: false });
}

function updateDroneAttackRun(a, dt){
  if (!a.run) return false;
  if (!monsterAliveTargetable()){ a.run = null; return false; }
  a.run.t += dt / a.run.dur;
  const k = Math.min(1, a.run.t);
  const p = a.run.curve.getPoint(k);
  const next = a.run.curve.getPoint(Math.min(1, k + .035));
  const target = monsterChest();
  a.pos.copy(p);
  a.g.position.copy(a.pos);
  const face = k < .75 && target ? target : next;
  a.g.rotation.y = lerpAngle(a.g.rotation.y, yawFromDir(face.x - a.pos.x, face.z - a.pos.z), dt*9);
  a.g.rotation.z = THREE.MathUtils.lerp(a.g.rotation.z, a.run.side*Math.sin(k*Math.PI)*.62, Math.min(1, dt*8));
  a.g.rotation.x = Math.sin(k*Math.PI*2)*.08;
  if (!a.run.fired && k > .38){
    a.run.fired = true;
    fireDroneVolley(a);
  }
  if (k >= 1){
    a.run = null;
    a.fireT = .62 + (a.attackLane || 0)*.15 + rng()*.22;
  }
  return true;
}

function botEngagePoint(a){
  if (!monster) return a.pos.clone();
  const away = monster.pos.clone();
  away.y = 0;
  if (away.lengthSq() < .01) away.set(a.slot || 1, 0, 1);
  away.normalize();
  const side = new THREE.Vector3(-away.z, 0, away.x).multiplyScalar((a.slot || 1)*8.5);
  const p = monster.pos.clone().addScaledVector(away, 15.5).add(side);
  p.x = THREE.MathUtils.clamp(p.x, -132, 132);
  p.z = THREE.MathUtils.clamp(p.z, -132, 132);
  return p;
}

function damageDrone(a){
  if (!a || a.state === 'docked' || a.state === 'launching' || a.state === 'destroyed' || a.state === 'falling') return;
  const source = `drone-${drones.indexOf(a) + 1}`;
  const hpBefore = a.hp ?? DRONE_MAX_HITS;
  a.hp = Math.max(0, (a.hp ?? DRONE_MAX_HITS) - 1);
  recordBattleEvent('droneDamaged', {
    source,
    hpBefore,
    hpAfter: a.hp,
    state: a.state,
    position: traceVec(a.pos),
  });
  sparkFx(a.pos.clone(), a.hp > 0 ? 0x9ff5dd : 0xff9d96);
  ringFx(a.pos, a.hp > 0 ? 0x9ff5dd : 0xff8e86, 2.8, .25, a.pos.y);
  if (a.hp > 0) return;
  const boom = a.pos.clone();
  explosionFx(boom, .82, 0xff9d96);
  addShake(.34);
  sfx.explode();
  a.state = 'falling';
  a.run = null;
  a.fallVel = new THREE.Vector3((Math.random()-.5)*7, 2.5, (Math.random()-.5)*7);
  a.fireT = 99;
  setHint('A drone was destroyed - HQ will re-equip after the threat');
}

function updateDrone(a, dt){
  const rotorSpeed = a.state === 'destroyed' ? 0 : a.state === 'docked' ? 3 : a.state === 'falling' ? 12 : 40;
  for (const r of a.rotors) r.rotation.y += dt * rotorSpeed;
  if (a.state === 'destroyed') return;
  if (a.state === 'falling'){
    a.fallVel.y -= 18*dt;
    a.pos.addScaledVector(a.fallVel, dt);
    const floorY = groundY(a.pos.x, a.pos.z) + .45;
    if (a.pos.y <= floorY){
      a.pos.y = floorY;
      a.g.position.copy(a.pos);
      a.state = 'destroyed';
      a.g.visible = false;
      puffFx(a.pos.clone().setY(floorY + .4), 10, 0xd8dce6, 8);
      sparkFx(a.pos.clone().setY(floorY + .8), 0xff9d96);
      return;
    }
    a.g.position.copy(a.pos);
    a.g.rotation.x += dt*5;
    a.g.rotation.z += dt*7;
    return;
  }
  if (a.state === 'launching'){
    a.run = null;
    a.timer -= dt;
    if (a.timer <= 0){ a.state = 'lifting'; }
    return;
  }
  if (a.state === 'lifting'){
    a.pos.y += dt*9;
    if (a.pos.y > a.pad.y + 8) a.state = 'engaging';
    a.g.position.copy(a.pos);
    return;
  }
  if (a.state === 'engaging'){
    if (!monsterAliveTargetable()){
      if (!monster) { a.run = null; a.state = 'returnPrep'; a.timer = .5; }
      // monster exists but not landed yet -> hold position
    }
    if (updateDroneAttackRun(a, dt)) return;
    a.orbitA += dt*a.orbitSpeed;
    const c = monster ? monster.g.position : new THREE.Vector3();
    const want = droneOrbitPoint(a, c);
    a.pos.lerp(want, Math.min(1, dt*2.6));
    a.g.position.copy(a.pos);
    if (monster){
      a.g.rotation.y = yawFromDir(c.x - a.pos.x, c.z - a.pos.z);
      a.g.rotation.z = Math.sin(a.orbitA*2.7 + a.phase)*.16;
      a.g.rotation.x = THREE.MathUtils.lerp(a.g.rotation.x, 0, Math.min(1, dt*5));
    }
    a.fireT -= dt;
    if (monsterAliveTargetable() && a.fireT <= 0 && a.pos.distanceTo(monsterChest()) < 22){
      startDroneAttackRun(a, c);
      a.fireT = 99;
    }
    return;
  }
  if (a.state === 'returnPrep'){ a.run = null; a.timer -= dt; if (a.timer <= 0) a.state = 'returning'; return; }
  if (a.state === 'returning'){
    a.run = null;
    const over = a.pad.clone().setY(a.pad.y + 7);
    const tgt = a.pos.distanceToSquared(over.clone().setY(a.pos.y)) > 1 ? over : a.pad.clone().add(new THREE.Vector3(0, .45, 0));
    a.pos.lerp(tgt, Math.min(1, dt*2.2));
    a.g.position.copy(a.pos);
    a.g.rotation.z *= .9;
    if (a.pos.distanceTo(a.pad) < .65){
      a.state = 'docked'; a.hp = DRONE_MAX_HITS; a.run = null; a.g.rotation.set(0,0,0);
    }
  }
}

function carBodyPlace(a, dt, speed){
  const done = walkPath(a, speed, dt);
  a.g.position.set(a.pos.x, groundY(a.pos.x, a.pos.z), a.pos.z);
  a.g.rotation.y = lerpAngle(a.g.rotation.y, a.heading, dt*8);
  return done;
}

function updateCop(a, dt){
  // light bar flash handled globally via copMintMat/copBlueMat
  if (a.state === 'launching'){
    a.timer -= dt;
    if (a.timer <= 0){
      a.g.visible = true;
      a.pos.copy(a.bay);
      a.path = [a.bay.clone(), new THREE.Vector3(a.bay.x, 0, 27), ...routeTo(new THREE.Vector3(a.bay.x, 0, 27), [nodeNearest(monster ? monster.pos : new THREE.Vector3())]).slice(1)];
      a.wp = 1; a.state = 'driving'; a.repathT = 1.5;
    }
    return;
  }
  if (a.state === 'driving'){
    if (!monsterAliveTargetable() && !monster){ a.state = 'returnPrep'; a.timer = .4; return; }
    const done = carBodyPlace(a, dt, 13);
    if (monster){
      const d = a.pos.distanceTo(monster.pos);
      if (d < 22){ a.state = 'engaging'; return; }
      a.repathT -= dt;
      if (done || a.repathT <= 0){
        a.repathT = 1.5;
        a.path = routeTo(a.pos, [nodeNearest(monster.pos)]);
        a.wp = 1;
      }
    } else if (done) a.state = 'returnPrep', a.timer = .3;
    return;
  }
  if (a.state === 'engaging'){
    if (!monster){ a.state = 'returnPrep'; a.timer = .5; return; }
    const d = a.pos.distanceTo(monster.pos);
    if (d > 27){ a.state = 'driving'; a.repathT = 0; return; }
    a.g.rotation.y = lerpAngle(a.g.rotation.y, yawFromDir(monster.pos.x - a.pos.x, monster.pos.z - a.pos.z), dt*4);
    a.fireT -= dt;
    if (monsterAliveTargetable() && a.fireT <= 0){
      a.fireT = 1.25;
      fireTracer(a.g.position.clone().add(new THREE.Vector3(0, 1.4, 0)), 6, tracerBlueMat, { source: `cop-${cops.indexOf(a) + 1}` });
    }
    return;
  }
  if (a.state === 'returnPrep'){ poseBotWeapon(a, dt, false); a.timer -= dt; if (a.timer <= 0){
    a.state = 'returning';
    a.path = [...routeTo(a.pos, [[2,3],[3,3]]), new THREE.Vector3(a.bay.x, 0, 27), a.bay.clone()];
    a.wp = 1;
  } return; }
  if (a.state === 'returning'){
    if (carBodyPlace(a, dt, 12)){
      a.state = 'docked'; a.g.visible = false;
      a.g.position.set(a.bay.x, groundY(a.bay.x, a.bay.z), a.bay.z);
    }
  }
}

function poseBotWeapon(a, dt, aiming = false, target = null){
  const k = a.shotT > 0 ? Math.sin((a.shotT / .48) * Math.PI) : 0;
  const step = Math.min(1, dt*14);
  const brace = aiming ? 1 : 0;
  const ease = (value, target) => value + (target - value) * step;
  let aimPitch = -.32, aimYaw = 0;

  if (aiming && target && a.gun){
    a.g.updateMatrixWorld(true);
    const localTarget = a.g.worldToLocal(target.clone());
    const rel = localTarget.sub(new THREE.Vector3(.1, 2.65, -.95));
    const flat = Math.max(.001, Math.hypot(rel.x, rel.z));
    aimPitch = THREE.MathUtils.clamp(Math.atan2(rel.y, flat), -.28, .48);
    aimYaw = THREE.MathUtils.clamp(Math.atan2(-rel.z, rel.x), -.7, .7);
  }

  if (a.gun){
    a.gun.position.x = ease(a.gun.position.x, .1);
    a.gun.position.y = ease(a.gun.position.y, 2.65);
    a.gun.position.z = ease(a.gun.position.z, -.95);
    a.gun.rotation.z = ease(a.gun.rotation.z, aimPitch + k*.06);
    a.gun.rotation.y = ease(a.gun.rotation.y, aimYaw);
  }
  if (a.muzzleGlow){
    a.muzzleGlow.visible = k > .02;
    a.muzzleGlow.scale.setScalar(.8 + k*5.2);
    a.muzzleGlow.material.opacity = .85*k;
  }
  a.g.rotation.z = ease(a.g.rotation.z, aiming ? -.03 - k*.05 : 0);
  if (aiming){
    a.hips.L.rotation.z = ease(a.hips.L.rotation.z, -.22 - k*.14);
    a.hips.R.rotation.z = ease(a.hips.R.rotation.z, .22 + k*.14);
  }
}

function updateBot(a, dt){
  a.shotT = Math.max(0, (a.shotT || 0) - dt);
  if (a.state === 'launching'){
    a.timer -= dt;
    if (a.timer <= 0){
      a.g.visible = true;
      a.pos.copy(a.bay);
      a.path = [a.bay.clone(), new THREE.Vector3(a.bay.x, 0, 27), ...routeTo(new THREE.Vector3(a.bay.x, 0, 27), [nodeNearest(monster ? monster.pos : new THREE.Vector3())]).slice(1)];
      a.wp = 1; a.state = 'marching'; a.repathT = 2;
    }
    return;
  }
  const stride = () => {
    a.walkT += dt*5; // slower cadence reads right at double scale
    a.hips.L.rotation.z = Math.sin(a.walkT)*.55;
    a.hips.R.rotation.z = -Math.sin(a.walkT)*.55;
    a.g.position.y += Math.abs(Math.sin(a.walkT))*.18;
  };
  if (a.state === 'marching'){
    poseBotWeapon(a, dt, false);
    if (!monster){ a.state = 'returnPrep'; a.timer = .4; return; }
    const d = a.pos.distanceTo(monster.pos);
    const hold = botEngagePoint(a);
    if (d < 18 || a.pos.distanceTo(hold) < 1.2){ a.state = 'engaging'; return; } // arm cannon outranges the drones' close orbit
    const done = carBodyPlace(a, dt, 6.2);
    stride();
    a.repathT -= dt;
    if (done || a.repathT <= 0){
      a.repathT = 2;
      if (d < 29){ a.path = [a.pos.clone(), hold]; a.wp = 1; } // final flank leg
      else { a.path = routeTo(a.pos, [nodeNearest(monster.pos)]); a.wp = 1; }
    }
    return;
  }
  if (a.state === 'engaging'){
    if (!monster){ a.state = 'returnPrep'; a.timer = .5; return; }
    const d = a.pos.distanceTo(monster.pos);
    if (d > 24){ a.state = 'marching'; a.repathT = 0; return; }
    const target = monsterChest();
    a.hips.L.rotation.z *= .85; a.hips.R.rotation.z *= .85;
    a.g.rotation.y = lerpAngle(a.g.rotation.y, yawFromDir(monster.pos.x - a.pos.x, monster.pos.z - a.pos.z), dt*5);
    poseBotWeapon(a, dt, true, target);
    a.fireT -= dt;
    if (monsterAliveTargetable() && a.fireT <= 0){
      a.fireT = 1.7;
      a.shotT = .48;
      a.g.rotation.y = yawFromDir(monster.pos.x - a.pos.x, monster.pos.z - a.pos.z);
      poseBotWeapon(a, Math.max(dt, 1/30), true, target);
      a.g.updateMatrixWorld(true);
      const from = new THREE.Vector3();
      a.muzzle.getWorldPosition(from);
      flashFx(from, 0xbfffe9, 1.35);
      beamFx(from, monsterChest, .42, 0xbfffe9, 2.25);
      beamFx(from, monsterChest, .28, 0x8cc8ff, 1.35); // second jagged strand adds energy color
      pulseDotsFx(from, target, 0xbfffe9, 5, 1.05);
      ringFx(from, 0x7df5d4, 4.1, .32, from.y);
      sfx.zap();
      impactBurstFx(target, 0xbfffe9, 1.25);
      damageMonster(10, target, from, `bot-${bots.indexOf(a) + 1}`);
    }
    return;
  }
  if (a.state === 'returnPrep'){ a.timer -= dt; if (a.timer <= 0){
    a.state = 'returning';
    a.path = [...routeTo(a.pos, [[2,3],[3,3]]), new THREE.Vector3(a.bay.x, 0, 27), a.bay.clone()];
    a.wp = 1;
  } return; }
  if (a.state === 'returning'){
    poseBotWeapon(a, dt, false);
    const done = carBodyPlace(a, dt, 6.2);
    stride();
    if (done){ a.state = 'docked'; a.g.visible = false;
      a.g.position.set(a.bay.x, groundY(a.bay.x, a.bay.z), a.bay.z); }
  }
}

function finishBattleReady(text = 'All clear - click to drop another monster'){
  setHint(text);
  stage.classList.remove('busy');
  flyToPose(root.classList.contains('is-complete') ? 'outcome' : 'hero');
}

function survivingDronesReturned(){
  return drones.every(d => d.state === 'docked' || d.state === 'destroyed');
}

function hqDroneReturnFx(){
  ringFx(new THREE.Vector3(0,0,0), 0x7df5d4, 24, .85, 39.2);
  ringFx(new THREE.Vector3(0,0,0), 0xbffbe8, 18, .7, 16);
  for (const d of drones){
    ringFx(d.pad, d.state === 'destroyed' ? 0xff9d96 : 0x9ff5dd, 3.2, .45, d.pad.y + .15);
  }
  sfx.chime();
}

function reEquipDestroyedDrones(){
  const lost = drones.filter(d => d.state === 'destroyed');
  hqDroneReturnFx();
  for (const d of lost){
    d.hp = DRONE_MAX_HITS;
    d.fireT = 0;
    d.timer = 0;
    d.fallVel = null;
    d.pos.copy(d.pad);
    d.g.position.copy(d.pad);
    d.g.rotation.set(0,0,0);
    d.g.visible = true;
    d.state = 'docked';
  }
  game.reequipPending = false;
  finishBattleReady(lost.length ? 'HQ re-equipped new drones - all clear' : undefined);
}

function updateHqRecovery(){
  if (!game.reequipPending || monster || !survivingDronesReturned()) return;
  reEquipDestroyedDrones();
}

/* ============================================================ */
/* 12. GAME STATE / ALARM                                       */
/* ============================================================ */
const game = { mode: 'calm', kills: 0, alarm: 0, alarmTarget: 0, t: 0, reequipPending: false };
let shieldPulse = 0, simming = false;

const hintEl = root.querySelector('[data-city-hint]');
const alarmEdge = root.querySelector('[data-city-alarm-edge]');

function setChip(){}
let hintTimer = null;
function setHint(text, deny){
  hintEl.textContent = text;
  hintEl.classList.toggle('deny', !!deny);
  if (deny){
    clearTimeout(hintTimer);
    hintTimer = setTimeout(() => {
      hintEl.classList.remove('deny');
      hintEl.textContent = monster ? 'Agents engaging - defeat the monster to spawn another' : 'Click anywhere in the city to drop a monster';
    }, 1500);
  }
}

function triggerAlarm(){
  game.mode = 'alert';
  game.alarmTarget = 1;
  setChip('alert');
  setHint('HQ alert - drones, patrol cars and mechs are responding');
  ringFx(new THREE.Vector3(0,0,0), 0x7df5d4, 26, .9, 39.2);
  ringFx(new THREE.Vector3(0,0,0), 0xffb3ad, 30, 1.1, .15);
  dispatchAgents();
  for (const p of pairs) if (p.state === 'talk'){ p.state = 'fleeing'; p.a.userData.bub.material = bubbleMatAlert; p.a.userData.bub.scale.set(1.05,.85,1); p.b.userData.bub.scale.set(0,0,1); }
  sfx.alarmOn();
}

function setAlarm(on){
  game.alarmTarget = on ? 1 : 0;
  if (!on){ game.mode = 'calm'; sfx.alarmOff(); }
}

/* ============================================================ */
/* 13. INPUT                                                    */
/* ============================================================ */
const raycaster = new THREE.Raycaster();
const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const ndc = new THREE.Vector2();

// click spawns; drags (free-camera moves) don't
let downPt = null;
const dropInputActive = () => root.classList.contains('is-targeting');
canvas.addEventListener('pointerdown', e => {
  sfx.unlock();
  if (!dropInputActive()){
    downPt = null;
    return;
  }
  downPt = { x: e.clientX, y: e.clientY };
});
canvas.addEventListener('pointerup', e => {
  if (!dropInputActive()){
    downPt = null;
    return;
  }
  if (!downPt) return;
  const moved = Math.hypot(e.clientX - downPt.x, e.clientY - downPt.y);
  downPt = null;
  if (moved > 6) return; // that was a camera drag
  if (monster){
    setHint('Agents are still engaged - wait for the all clear', true);
    return;
  }
  const r = canvas.getBoundingClientRect();
  ndc.set(((e.clientX - r.left)/r.width)*2 - 1, -((e.clientY - r.top)/r.height)*2 + 1);
  raycaster.setFromCamera(ndc, camera);
  const hit = new THREE.Vector3();
  if (!raycaster.ray.intersectPlane(groundPlane, hit)) return;
  const targetedDrop = root.classList.contains('is-targeting');
  if (!targetedDrop) return;
  hit.x = THREE.MathUtils.clamp(hit.x, -PLAT_HALF, PLAT_HALF);
  hit.z = THREE.MathUtils.clamp(hit.z, -PLAT_HALF, PLAT_HALF);
  if (spawnMonster(hit, targetedDrop ? 'drop' : undefined)) stage.classList.add('busy');
});

/* ============================================================ */
/* 14. MAIN UPDATE                                              */
/* ============================================================ */
function updatePairs(dt){
  for (const p of pairs){
    if (p.state === 'talk'){
      p.talkT -= dt;
      if (p.talkT <= 0){
        p.talkT = 1.3 + Math.random()*1.4;
        p.who = 1 - p.who;
      }
      p.a.userData.bub.position.y = 2.0 + Math.sin(game.t*2.2)*.06;
      p.b.userData.bub.position.y = 2.0 + Math.sin(game.t*2.2 + 1)*.06;
      const act = p.who ? p.a : p.b, idle = p.who ? p.b : p.a;
      act.userData.bub.material = bubbleMatDots;
      act.userData.bub.scale.lerp(new THREE.Vector3(1.55, 1.26, 1), dt*10);
      idle.userData.bub.scale.lerp(new THREE.Vector3(0, 0, 1), dt*10);
      act.position.y = p.y + Math.abs(Math.sin(game.t*5 + p.talkT))*.04;
    } else if (p.state === 'fleeing'){
      let arrived = true;
      for (const [person, home] of [[p.a, p.homeA], [p.b, p.homeB]]){
        const dir = p.flee.clone().sub(person.position); dir.y = 0;
        if (dir.length() > .4){
          arrived = false;
          dir.normalize();
          person.position.addScaledVector(dir, dt*3.4);
          person.rotation.y = yawFromDir(dir.x, dir.z) + Math.PI/2;
          person.position.y = p.y + Math.abs(Math.sin(game.t*14))*.09;
          person.userData.bub.scale.lerp(new THREE.Vector3(0,0,1), dt*6);
        }
      }
      if (arrived){ p.state = 'hidden'; p.a.visible = p.b.visible = false; }
    } else if (p.state === 'hidden' && game.alarm < .08){
      p.state = 'talk';
      p.a.visible = p.b.visible = true;
      p.a.position.copy(p.homeA); p.b.position.copy(p.homeB);
      p.a.lookAt(p.homeB.x, p.homeB.y, p.homeB.z);
      p.b.lookAt(p.homeA.x, p.homeA.y, p.homeA.z);
      p.a.userData.bub.material = bubbleMatDots;
    }
  }
}

function update(dt){
  const rawDt = dt;
  dt *= updateCinematicClock(rawDt);
  recordBattleFrame(rawDt, dt);
  game.t += dt;
  const t = game.t;
  uTime.value = t; // drives the holo sweep + shader water

  // alarm: red corner vignette + HQ beacons + cop light bars - the city keeps its mint glow
  game.alarm += (game.alarmTarget - game.alarm) * Math.min(1, dt*2.6);
  const a = game.alarm;
  beaconMat.emissiveIntensity = a > .1 ? (Math.sin(t*10) > 0 ? 3 : .2) : (Math.sin(t*1.6) > .92 ? 1.4 : .06);
  copBlueMat.emissiveIntensity = a > .1 ? (Math.sin(t*12) > 0 ? 3.2 : .35) : 1.2;
  copMintMat.emissiveIntensity = a > .1 ? (Math.sin(t*12) > 0 ? .35 : 3.2) : 1.2;
  alarmEdge.style.opacity = (a * (0.72 + Math.sin(t*6)*.28)).toFixed(2);
  if (game.mode === 'alert') sfx.alarmTick(Math.floor(t/.45) % 2 === 0);

  shieldPulse = Math.max(0, shieldPulse - dt*2.4);
  shieldMat.opacity = a*(.13 + Math.sin(t*2.8)*.03) + shieldPulse*.3;

  // ambient animation hooks from the asset builders (radar sweep, mech bars)
  for (const f of ticks) f(dt, t, a);

  // ambient life - the monorail eases into each station, dwells, then departs
  if (train.dwell > 0){ train.dwell -= dt; }
  else {
    let ahead = 1e9, si = -1;
    for (let i = 0; i < STATION_DS.length; i++){
      const adv = ((STATION_DS[i] - train.d) % railLoop.len + railLoop.len) % railLoop.len;
      if (adv < ahead){ ahead = adv; si = i; }
    }
    const stopping = si !== train.lastStop; // don't re-stop at the platform just left
    const v = stopping && ahead < 30 ? Math.max(2.0, train.speed*(ahead/30)) : train.speed;
    train.d += dt * v * (1 + a*.4);
    if (stopping && ahead < .8){ train.dwell = 3.2; train.lastStop = si; }
  }
  train.cars.forEach((c, i) => {
    const p = railLoop.at(train.d - i*4.3);
    c.position.set(p.x, RAIL_Y + .95, p.z);
    c.rotation.y = yawFromDir(p.tx, p.tz);
  });
  for (const c of traffic){
    if ((c.disabledT || 0) > 0){
      c.disabledT = Math.max(0, c.disabledT - dt);
      c.g.visible = c.disabledT <= 0;
    }
    c.d += dt * c.lane.speed * c.lane.dir * (1 + a*1.3);
    const p = c.lane.loop.at(c.d);
    c.g.position.set(p.x, 0, p.z);
    c.g.rotation.y = yawFromDir(p.tx*c.lane.dir, p.tz*c.lane.dir);
  }
  for (const cl of clouds){
    cl.position.x += dt*.85;
    if (cl.position.x > 150) cl.position.x = -150;
  }
  shuttle.position.x += dt*22;
  if (shuttle.position.x > 320) shuttle.position.x = -320;
  updatePairs(dt);

  // combat
  updateMonster(dt);
  for (const d of drones) updateDrone(d, dt);
  for (const c of cops) updateCop(c, dt);
  for (const b of bots) updateBot(b, dt);
  updateHqRecovery();

  // tracers (homing bolts)
  for (let i = tracers.length-1; i >= 0; i--){
    const tr = tracers[i];
    tr.t += dt/tr.dur;
    const tgt = monsterChest();
    if (!tgt){ scene.remove(tr.m); tracers.splice(i,1); continue; }
    tr.m.position.lerpVectors(tr.from, tgt, Math.min(1, tr.t));
    tr.m.lookAt(tgt);
    if (tr.t >= 1){
      scene.remove(tr.m); tracers.splice(i,1);
      impactBurstFx(tgt, tr.color, .92);
      damageMonster(tr.dmg, tgt, tr.from, tr.source);
    }
  }

  // ephemeral fx
  for (let i = fx.length-1; i >= 0; i--) if (!fx[i].update(dt)) fx.splice(i,1);

  // hybrid camera: event fly-to + player orbit + impact shake
  camera.position.sub(appliedShake);
  shake = Math.max(0, shake - rawDt*1.8);
  if (camAnim){
    const ck = 1 - Math.exp(-rawDt*(camAnim.rate || 1.7));
    camera.position.lerp(camAnim.pos, ck);
    orbit.target.lerp(camAnim.look, ck);
    if (camera.position.distanceTo(camAnim.pos) < .5) camAnim = null;
  }
  updateMonsterCameraFollow(rawDt);
  orbit.update();
  appliedShake.set((Math.random()-.5)*shake*1.6, (Math.random()-.5)*shake*1.2, (Math.random()-.5)*shake*1.6);
  camera.position.add(appliedShake);
  // haze follows the camera so the far city always dissolves toward the horizon
  const cd = camera.position.distanceTo(orbit.target);
  scene.fog.near = cd*1.35;
  scene.fog.far = cd*1.35 + 560;
}

/* ============================================================ */
/* 15. LOOP + TEST API                                          */
/* ============================================================ */
let paused = root.dataset.paused === 'true';
let destroyed = false;
const clock = new THREE.Clock();
let firstFrame = true;
let frameId = 0;
let inViewport = true;
let needsRender = true;

function canRunFrame(){
  return !destroyed && inViewport && document.visibilityState !== 'hidden';
}

function requestFrame(){
  if (!frameId && canRunFrame()) frameId = requestAnimationFrame(frame);
}

function frame(){
  frameId = 0;
  if (destroyed) return;
  if (!canRunFrame()) return;
  const dt = Math.min(.066, clock.getDelta());
  if (!paused) update(dt);
  if (!paused || needsRender) {
    composer.render();
    needsRender = false;
  }
  if (firstFrame){
    firstFrame = false;
    root.dataset.cityLoaded = 'true';
    if (loadingEl) loadingEl.setAttribute('aria-hidden', 'true');
  }
  if (!paused) requestFrame();
}

const visibilityHandler = () => {
  clock.getDelta();
  needsRender = true;
  requestFrame();
};
document.addEventListener('visibilitychange', visibilityHandler);

let intersectionObserver;
if ('IntersectionObserver' in window) {
  intersectionObserver = new IntersectionObserver(entries => {
    inViewport = entries.some(entry => entry.isIntersecting);
    visibilityHandler();
  }, { rootMargin: '240px 0px' });
  intersectionObserver.observe(root);
}

requestFrame();

function roundVec(v){ return v.toArray().map(n => +n.toFixed(2)); }
function vecArgs(v){ return v.map(n => Number.isInteger(n) ? String(n) : n.toFixed(2)).join(', '); }
function cameraSnapshot(){
  const pos = roundVec(camera.position);
  const look = roundVec(orbit.target);
  const poseLabel = activeCameraPoseName === 'outcome' ? 'result' : (activeCameraPoseName || 'custom');
  return {
    breakpoint: activeCameraBreakpoint,
    pose: poseLabel,
    dist: +camera.position.distanceTo(orbit.target).toFixed(1),
    pos,
    target: look,
    look,
    fov: +camera.fov.toFixed(2),
    zoom: +camera.zoom.toFixed(2),
    renderPixelRatio: +activeRenderPixelRatio.toFixed(2),
    setCam: `window.__cityHeroGame.setCam(${vecArgs([...pos, ...look])})`,
  };
}
function cameraPoseSummary(){
  const out = {};
  Object.entries(CAMERA_POSES).forEach(([breakpoint, poses]) => {
    out[breakpoint] = {};
    Object.entries(poses).forEach(([name, pose]) => {
      out[breakpoint][name] = { pos: roundVec(pose.pos), look: roundVec(pose.look) };
    });
    out[breakpoint].result = out[breakpoint].outcome;
  });
  return out;
}

// deterministic hooks for automated verification
const gameApi = {
  startTrace(label){ return startBattleTrace(label); },
  trace(){ return battleTraceSnapshot(); },
  clearTrace(){ battleTrace = null; return null; },
  battleLogs(){ return storedBattleLogs(); },
  battleLogText(){ return battleLogText(); },
  clearBattleLogs(){ localStorage.removeItem(BATTLE_LOG_KEY); battleTrace = null; return []; },
  spawnAt(x, z, mode){ return spawnMonster(new THREE.Vector3(x, 0, z), mode); },
  sim(seconds){
    simming = true; const n = Math.ceil(seconds*30);
    for (let i=0;i<n;i++) update(1/30);
    simming = false; composer.render();
  },
  setPaused(v){
    paused = !!v;
    needsRender = true;
    clock.getDelta();
    requestFrame();
  },
  setGameTime(seconds){
    game.t = Number.isFinite(+seconds) ? +seconds : 0;
    clock.getDelta();
    return game.t;
  },
  setTune(p){
    Object.assign(TUNE, p);
    applyTune();
    needsRender = true;
    requestFrame();
    return { ...TUNE };
  },
  flyHome(){ followMonsterCamera = false; orbit.minDistance = 90; flyToPose(monster ? 'tactical' : 'hero'); },
  flyOutcome(){
    followMonsterCamera = false;
    orbit.minDistance = 1;
    flyToPose('outcome');
    needsRender = true;
    requestFrame();
  },
  setCameraPose(name){
    const normalized = name === 'result' ? 'outcome' : name;
    if (!POSE[normalized] && !CAMERA_POSES.desktop[normalized]) return null;
    followMonsterCamera = false;
    orbit.minDistance = normalized === 'outcome' ? 1 : 90;
    flyToPose(normalized);
    needsRender = true;
    requestFrame();
    return cameraSnapshot();
  },
  setCam(px, py, pz, tx, ty, tz){
    followMonsterCamera = false;
    camAnim = null; cinematic = null; activeCameraPoseName = null; orbit.minDistance = 1;
    camera.position.set(px, py, pz); orbit.target.set(tx, ty, tz);
    orbit.update(); composer.render();
    needsRender = true;
    requestFrame();
    return cameraSnapshot();
  },
  cam(){ return cameraSnapshot(); },
  cameraPoses(){ return cameraPoseSummary(); },
  scene, // exposed for the headless test harness
  THREE,
  state(){
    return {
      mode: game.mode, alarm: +game.alarm.toFixed(2), kills: game.kills,
      reequipPending: game.reequipPending,
      cinematic: cinematic ? { timeScale: cinematic.timeScale, t: +cinematic.t.toFixed(2) } : null,
      monster: monster ? { hp: monster.hp, maxHp: monster.maxHp, state: monster.state,
        berserk: monster.berserk, speed: +monster.speed.toFixed(1),
        x: +monster.pos.x.toFixed(1), z: +monster.pos.z.toFixed(1) } : null,
      drones: drones.map(d => ({ state: d.state, hp: d.hp, running: !!d.run })),
      droneRuns: drones.filter(d => d.run).length,
      botShots: bots.map(b => +(b.shotT || 0).toFixed(2)),
      botPositions: bots.map(b => ({ x: +b.pos.x.toFixed(1), z: +b.pos.z.toFixed(1), state: b.state })),
      disabledCars: traffic.filter(c => (c.disabledT || 0) > 0).length,
      hiddenPairs: pairs.filter(p => p.state === 'hidden').length,
      agents: allAgents().map(a => a.kind + ':' + a.state),
      train: { d: +train.d.toFixed(1), dwell: +train.dwell.toFixed(2), lastStop: train.lastStop },
    };
  },
};

root.__cityHeroGame = gameApi;
window.__cityHeroGame = gameApi;
		root.__cityHeroDestroy = function(){
		  destroyed = true;
		  paused = true;
		  if (frameId) cancelAnimationFrame(frameId);
      clearTimeout(hintTimer);
	  resizeObserver.disconnect();
	  if (intersectionObserver) intersectionObserver.disconnect();
	  document.removeEventListener('visibilitychange', visibilityHandler);
      orbitInputEvents.forEach(name => root.removeEventListener(name, syncOrbitInput));
	  orbit.dispose();
	  if (composer && typeof composer.dispose === 'function') composer.dispose();
	  renderer.dispose();
	};

}

function boot(scope = document){
  scope.querySelectorAll('[data-city-hero]').forEach(initCityHero);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot());
} else {
  boot();
}

document.addEventListener('shopify:section:load', event => boot(event.target));
document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('[data-city-hero]').forEach(root => {
    if (root.__cityHeroDestroy) root.__cityHeroDestroy();
    root.__cityHeroReady = false;
  });
});
